import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { getResultTextColor, getResultLabel, shouldShowResult } from '@/utils/checkResultColors';
import { EmailTemplatesTab } from '../components/operations/EmailTemplatesTab';
import { TATBreachAlertsTab } from '../components/operations/TATBreachAlertsTab';
import { VendorPricingDialog } from '../components/operations/VendorPricingDialog';
import { VendorPricingComparison } from '../components/operations/VendorPricingComparison';
import BulkDocumentUpload from '@/components/shared/BulkDocumentUpload';
import { 
  Search, Filter, Clock, CheckCircle, AlertTriangle, RefreshCw,
  PlusCircle, AlertCircle, FileText, Send, RotateCcw, Eye, Users, Building2,
  Calendar, TrendingUp, TrendingDown, BarChart3, Zap, UserPlus, Scale,
  ClipboardList, Settings, LogOut, GraduationCap, ExternalLink, X, Download, User, Database, Mail, Edit, Trash2, Copy, DollarSign, MapPin, Upload, FileUp, Percent, FileCheck, FolderUp, KeyRound, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import NotificationCenter from '@/components/shared/NotificationCenter';
import { formatDateDDMMYYYY } from '@/utils/textFormatting';
import * as XLSX from 'xlsx';

// Indian States List
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry',
  'Chandigarh', 'Dadra and Nagar Haveli', 'Daman and Diu',
  'Lakshadweep', 'Andaman and Nicobar Islands'
];

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification' },
  { value: 'employment_check', label: 'Employment Verification' },
  { value: 'education_check', label: 'Education Verification' },
  { value: 'id_check', label: 'ID Verification' },
  { value: 'criminal_court_check', label: 'Criminal Check' },
  { value: 'criminal_advocate_check', label: 'Criminal Advocate' },
  { value: 'credit_check', label: 'Credit Check' },
  { value: 'reference_check', label: 'Reference Check' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'global_database_check', label: 'Global Database' },
];

const INSUFFICIENCY_REASONS = [
  { value: 'employer_request', label: 'Employer Requests More Info' },
  { value: 'university_request', label: 'University Requests More Info' },
  { value: 'document_unclear', label: 'Document Unclear/Illegible' },
  { value: 'info_mismatch', label: 'Information Mismatch' },
  { value: 'missing_document', label: 'Missing Document' },
  { value: 'contact_unreachable', label: 'Contact Unreachable' },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-orange-100 text-orange-800',
  assigned: 'bg-purple-100 text-purple-800',
  vendor_assigned: 'bg-indigo-100 text-indigo-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  discrepant: 'bg-red-100 text-red-800',
};

export default function OperationsManagerDashboard({ user, onLogout, onSwitchAccount }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({});
  const [insufficiencies, setInsufficiencies] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [workloadData, setWorkloadData] = useState([]);
  const [returnedChecks, setReturnedChecks] = useState([]);
  const [vendorTeamChecks, setVendorTeamChecks] = useState([]);
  const [internalTeamChecks, setInternalTeamChecks] = useState([]);
  const [verificationTeams, setVerificationTeams] = useState([]);
  
  // Pagination state
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Global Completed tab — state lives separately from `checks`
  // because it pulls the full Cross-vendor list from /team/checks
  // (which carries resolved_state/city/pincode + vendor_cost). May 30, 2026.
  const [completedGlobalChecks, setCompletedGlobalChecks] = useState([]);
  const [completedGlobalLoading, setCompletedGlobalLoading] = useState(false);
  const [completedGlobalDateFilter, setCompletedGlobalDateFilter] = useState('latest');
  const [completedGlobalDateFrom, setCompletedGlobalDateFrom] = useState('');
  const [completedGlobalDateTo, setCompletedGlobalDateTo] = useState('');
  const [completedGlobalVendorFilter, setCompletedGlobalVendorFilter] = useState('all');
  const [completedGlobalClientFilter, setCompletedGlobalClientFilter] = useState('all');
  const [completedGlobalCheckTypeFilter, setCompletedGlobalCheckTypeFilter] = useState('all');
  const [completedGlobalExportLoading, setCompletedGlobalExportLoading] = useState(false);
  
  // Drill-down state for stats cards
  const [drillDownType, setDrillDownType] = useState(null); // 'unassigned', 'newly_added', 'in_progress', 'within_tat', 'out_of_tat', 'completed', 'insuff_raised', 'insuff_cleared'
  const [showDrillDown, setShowDrillDown] = useState(false);
  
  // Auto-assign settings
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [showAutoAssignSettings, setShowAutoAssignSettings] = useState(false);
  const [autoAssignSettings, setAutoAssignSettings] = useState({
    auto_assign_enabled: false,
    round_robin: true,
    respect_specializations: true,
    workload_balancing: true,
    max_checks_per_agent: 20
  });
  
  // Verification Mode Settings
  const [verificationModeSettings, setVerificationModeSettings] = useState({
    verification_mode: 'manual',
    auto_mode_enabled: false,
    auto_green_on_all_verified: true,
    default_remarks: [],
    check_type_remarks: {}
  });
  
  // Filters
  const [filters, setFilters] = useState({
    clientId: 'all',
    checkType: 'all',
    tatStatus: 'all',
    status: 'all',
    teamType: 'all', // New: filter by team type (internal/vendor)
    agentId: 'all',
    vendorId: 'all',
    // Date filter type: 'latest', 'today', 'this_week', 'this_month', 'this_year', 'date_range'
    dateFilterType: 'latest',
    // Received Date = QC Approved Date
    receivedFrom: '',
    receivedTo: '',
    // Assigned Date = Sent to Vendor/Company/University
    assignedFrom: '',
    assignedTo: '',
    // Completed Date = Verified Date
    completedFrom: '',
    completedTo: '',
    search: '',
  });
  
  // Filter checks client-side for team type, vendor, and status filters
  const getFilteredChecks = () => {
    return checks.filter(check => {
      // Verification Status filter
      if (filters.status && filters.status !== 'all') {
        const verificationProgress = check.verification_progress || '';
        const checkStatus = check.status || '';
        
        if (filters.status === 'not_initiated') {
          // Not initiated = verification_progress is 'not_initiated' OR empty/null OR status is 'pending_ops_assignment'
          const isNotInitiated = !verificationProgress || 
            verificationProgress === '' || 
            verificationProgress === 'not_initiated' ||
            checkStatus === 'pending_ops_assignment' ||
            checkStatus === 'pending';
          if (!isNotInitiated) return false;
        } else if (filters.status === 'in_progress') {
          // In progress = verification_progress indicates active work
          const isInProgress = verificationProgress === 'in_progress' || 
            verificationProgress === 'initiated' ||
            checkStatus === 'in_progress' ||
            checkStatus === 'assigned';
          if (!isInProgress) return false;
        } else if (filters.status === 'completed') {
          // Completed = verified or completed
          const isCompleted = verificationProgress === 'completed' || 
            verificationProgress === 'verified' ||
            checkStatus === 'verified' ||
            checkStatus === 'completed';
          if (!isCompleted) return false;
        }
      }
      
      // Team type filter
      if (filters.teamType && filters.teamType !== 'all') {
        const teamType = check.assigned_agent_team_type || '';
        const agentName = check.assigned_agent_name || '';
        
        if (filters.teamType === 'internal') {
          const isInternal = ['inhouse_verification', 'internal', 'inhouse'].some(t => 
            teamType.toLowerCase().includes(t)
          ) || agentName.toLowerCase().includes('internal team');
          if (!isInternal) return false;
        } else if (filters.teamType === 'vendor') {
          const isVendor = ['vendor_management', 'vendor'].some(t => 
            teamType.toLowerCase().includes(t)
          ) || agentName.toLowerCase().includes('vendor team');
          if (!isVendor) return false;
        }
      }
      // Vendor filter
      if (filters.vendorId && filters.vendorId !== 'all') {
        if (filters.vendorId === 'unassigned') {
          if (check.vendor_name) return false;
        } else {
          const vendorMatch = vendors.find(v => v.id === filters.vendorId);
          if (vendorMatch && check.vendor_name !== vendorMatch.name) return false;
        }
      }
      return true;
    });
  };
  
  // Get paginated checks
  const getPaginatedChecks = (filteredChecks) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredChecks.slice(startIndex, endIndex);
  };
  
  // Quick filter helper functions
  const getDateRangeForFilter = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filterType) {
      case 'today':
        return { from: today.toISOString().split('T')[0], to: today.toISOString().split('T')[0] };
      case 'this_week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Sunday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Saturday
        return { from: weekStart.toISOString().split('T')[0], to: weekEnd.toISOString().split('T')[0] };
      }
      case 'this_month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { from: monthStart.toISOString().split('T')[0], to: monthEnd.toISOString().split('T')[0] };
      }
      case 'this_year': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        return { from: yearStart.toISOString().split('T')[0], to: yearEnd.toISOString().split('T')[0] };
      }
      case 'latest':
      case 'date_range':
      default:
        return { from: '', to: '' };
    }
  };
  
  const handleQuickFilterChange = (filterType) => {
    const dateRange = getDateRangeForFilter(filterType);
    setFilters(prev => ({
      ...prev,
      dateFilterType: filterType,
      receivedFrom: filterType !== 'date_range' && filterType !== 'latest' ? dateRange.from : prev.receivedFrom,
      receivedTo: filterType !== 'date_range' && filterType !== 'latest' ? dateRange.to : prev.receivedTo,
      assignedFrom: '',
      assignedTo: '',
      completedFrom: '',
      completedTo: '',
    }));
  };
  
  // Selection for bulk actions
  const [selectedChecks, setSelectedChecks] = useState([]);
  
  // Dialogs
  const [showRaiseInsuff, setShowRaiseInsuff] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [insuffData, setInsuffData] = useState({
    reason: '',
    description: '',
    targetTeam: 'qc',
    requestedDocuments: [],
    requestedInfo: [],
  });
  
  // Assign dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignType, setAssignType] = useState('agent');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [isReassign, setIsReassign] = useState(false); // Track if this is a reassign action
  
  // Reinitiate dialog
  const [showReinitiate, setShowReinitiate] = useState(false);
  const [selectedInsuff, setSelectedInsuff] = useState(null);
  const [reinitiateData, setReinitiateData] = useState({
    target: 'hr',
    notes: '',
  });

  // Document preview state
  const [showCheckDetails, setShowCheckDetails] = useState(false);
  const [checkDocuments, setCheckDocuments] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [checkDetailData, setCheckDetailData] = useState(null); // Full check detail with employee data
  const [selectedSubcheck, setSelectedSubcheck] = useState(null); // For subcheck selection

  // Auto-Initiate state
  const [showAutoInitiate, setShowAutoInitiate] = useState(false);
  const [autoInitiateCheck, setAutoInitiateCheck] = useState(null);
  const [autoInitiateResult, setAutoInitiateResult] = useState(null);
  const [autoInitiateLoading, setAutoInitiateLoading] = useState(false);
  
  // Manual contact entry state (when auto-initiate not available)
  const [manualContactEmail, setManualContactEmail] = useState('');
  const [manualContactName, setManualContactName] = useState('');
  const [saveToDatabase, setSaveToDatabase] = useState(true);

  // Company and University Database
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const [institutionTemplates, setInstitutionTemplates] = useState([]);
  const [dbSearch, setDbSearch] = useState('');

  // Vendor Management state
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [vendorFormData, setVendorFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    vendor_type: 'subvendor',
    gst_number: '',
    pan_number: '',
    coverage_type: 'pan_india',
    coverage_states: [],
    coverage_cities: [],
    specializations: [],
    tat_days: 7,
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder: '',
    check_pricing: [],
    additional_contacts: [],
    check_tat: {},  // TAT per check type
    // New fields
    agreement_file: null,
    agreement_validity: '',
    tat_penalties: [
      { period_start: 6, period_end: 10, penalty_percent: 5 },
      { period_start: 11, period_end: 15, penalty_percent: 10 },
      { period_start: 16, period_end: 20, penalty_percent: 15 },
      { period_start: 21, period_end: null, penalty_percent: 20 }
    ]
  });
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorFilter, setVendorFilter] = useState('all');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSort, setVendorSort] = useState('name_asc');
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [selectedVendorForPricing, setSelectedVendorForPricing] = useState(null);
  const [showPricingComparison, setShowPricingComparison] = useState(false);
  const [showContactsDialog, setShowContactsDialog] = useState(false);
  const [selectedVendorForContacts, setSelectedVendorForContacts] = useState(null);
  const [vendorContacts, setVendorContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', designation: '', department: '', cc_emails: true, cc_notifications: true });
  const [showTatDialog, setShowTatDialog] = useState(false);
  const [selectedVendorForTat, setSelectedVendorForTat] = useState(null);
  const [vendorTatConfig, setVendorTatConfig] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showPenaltyDialog, setShowPenaltyDialog] = useState(false);
  const [selectedVendorForPenalty, setSelectedVendorForPenalty] = useState(null);

  // Field Forms Management state
  const [vendorManagementTab, setVendorManagementTab] = useState('vendors');
  const [fieldForms, setFieldForms] = useState([]);
  const [showFieldFormDialog, setShowFieldFormDialog] = useState(false);
  const [editingFieldForm, setEditingFieldForm] = useState(null);
  const [fieldFormData, setFieldFormData] = useState({
    name: '',
    check_type: '',
    description: '',
    fields: [],
    is_active: true,
    mapped_clients: []
  });

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-500' },
    { id: 'checks', label: 'All Checks', icon: FileText, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-500' },
    { id: 'completed', label: 'Completed (Global)', icon: CheckCircle, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-500' },
    { id: 'returned-checks', label: 'Returned Checks', icon: RotateCcw, color: 'amber', bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-500' },
    { id: 'tat-alerts', label: 'TAT Alerts', icon: AlertTriangle, color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-500' },
    { id: 'vendor-management', label: 'Vendor Management', icon: Building2, color: 'teal', bgColor: 'bg-teal-50', textColor: 'text-teal-700', borderColor: 'border-teal-500' },
    { id: 'check-types', label: 'Check Types', icon: ClipboardList, color: 'pink', bgColor: 'bg-pink-50', textColor: 'text-pink-700', borderColor: 'border-pink-500' },
    { id: 'email-templates', label: 'Email Templates', icon: Mail, color: 'sky', bgColor: 'bg-sky-50', textColor: 'text-sky-700', borderColor: 'border-sky-500' },
    { id: 'vendor-team', label: 'Agent Vendor Team', icon: Building2, color: 'violet', bgColor: 'bg-violet-50', textColor: 'text-violet-700', borderColor: 'border-violet-500' },
    { id: 'internal-team', label: 'Agent Internal Team', icon: Users, color: 'cyan', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700', borderColor: 'border-cyan-500' },
    { id: 'company-db', label: 'Company Database', icon: Building2, color: 'indigo', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-500' },
    { id: 'university-db', label: 'University Database', icon: GraduationCap, color: 'rose', bgColor: 'bg-rose-50', textColor: 'text-rose-700', borderColor: 'border-rose-500' },
    { id: 'workload', label: 'Workload Balance', icon: Scale, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-500' },
    { id: 'insufficiencies', label: 'Insufficiencies', icon: AlertCircle, color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-500' },
    { id: 'form-management', label: 'Form Management', icon: FileCheck, color: 'fuchsia', bgColor: 'bg-fuchsia-50', textColor: 'text-fuchsia-700', borderColor: 'border-fuchsia-500' },
    { id: 'settings', label: 'Auto-Assign Settings', icon: Settings, color: 'slate', bgColor: 'bg-slate-100', textColor: 'text-slate-700', borderColor: 'border-slate-500' },
  ];

  // Check Types Management State
  const [checkTypesList, setCheckTypesList] = useState([]);
  const [showAddCheckType, setShowAddCheckType] = useState(false);
  const [newCheckType, setNewCheckType] = useState({
    id: '',
    name: '',
    description: '',
    category: 'verification',
    default_tat: 7,
    is_expandable: false,
    requires_documents: true
  });
  
  // Bulk Document Upload
  const [showBulkDocUpload, setShowBulkDocUpload] = useState(false);
  
  // Form Management State (additional state - fieldForms already defined above)
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formMappings, setFormMappings] = useState([]);

  useEffect(() => {
    fetchData();
    fetchAgentsAndVendors();
    fetchClients();
    fetchTeamsData();
    fetchAutoAssignSettings();
    fetchVerificationModeSettings();
    fetchCheckTypes();
    fetchFieldForms();
  }, []);

  // Auto-apply filters when they change (like Agent view)
  useEffect(() => {
    // Skip initial render - filters are already applied in fetchData
    const isInitialFilters = filters.clientId === 'all' && 
      filters.checkType === 'all' && 
      filters.tatStatus === 'all' && 
      filters.status === 'all' &&
      filters.teamType === 'all' &&
      filters.vendorId === 'all' &&
      filters.dateFilterType === 'latest' &&
      !filters.search;
    
    // Clear selections when filters change to prevent stale selections
    setSelectedChecks([]);
    
    // Don't auto-fetch on initial load, but do fetch when any filter changes
    if (!isInitialFilters || filters.search) {
      const debounceTimer = setTimeout(() => {
        fetchData();
      }, 300); // Debounce for search input
      
      return () => clearTimeout(debounceTimer);
    }
  }, [filters.clientId, filters.checkType, filters.tatStatus, filters.status, 
      filters.teamType, filters.vendorId, filters.dateFilterType, filters.search,
      filters.receivedFrom, filters.receivedTo, filters.assignedFrom, filters.assignedTo,
      filters.completedFrom, filters.completedTo]);

  const fetchCheckTypes = async () => {
    try {
      const res = await axios.get('/check-types');
      setCheckTypesList(res.data || []);
    } catch (error) {
      console.error('Failed to fetch check types:', error);
    }
  };

  // Field Forms Management Functions
  const fetchFieldForms = async () => {
    try {
      const res = await axios.get('/field-forms');
      setFieldForms(res.data || []);
    } catch (error) {
      console.error('Failed to fetch field forms:', error);
      // Set default forms if API doesn't exist yet
      setFieldForms([
        { id: 'address_form', name: 'Address Verification Form', check_type: 'address_check', fields_count: 12, mapped_clients: 5, is_active: true },
        { id: 'employment_form', name: 'Employment Verification Form', check_type: 'employment_check', fields_count: 15, mapped_clients: 8, is_active: true },
        { id: 'education_form', name: 'Education Verification Form', check_type: 'education_check', fields_count: 10, mapped_clients: 6, is_active: true },
      ]);
    }
  };

  const handleSaveFieldForm = async () => {
    try {
      if (editingFieldForm) {
        await axios.put(`/field-forms/${editingFieldForm.id}`, fieldFormData);
        toast.success('Field form updated successfully');
      } else {
        await axios.post('/field-forms', fieldFormData);
        toast.success('Field form created successfully');
      }
      setShowFieldFormDialog(false);
      setEditingFieldForm(null);
      fetchFieldForms();
    } catch (error) {
      toast.error('Failed to save field form');
    }
  };

  const handleCloneFieldForm = async (form) => {
    try {
      const clonedForm = {
        ...form,
        name: `${form.name} (Copy)`,
        id: undefined
      };
      await axios.post('/field-forms', clonedForm);
      toast.success('Field form cloned successfully');
      fetchFieldForms();
    } catch (error) {
      toast.error('Failed to clone field form');
    }
  };

  const handleDeleteFieldForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this field form?')) return;
    try {
      await axios.delete(`/field-forms/${formId}`);
      toast.success('Field form deleted');
      fetchFieldForms();
    } catch (error) {
      toast.error('Failed to delete field form');
    }
  };

  // Vendor Management Handler Functions
  const resetVendorForm = () => {
    setVendorFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      contact_person: '',
      gst_number: '',
      pan_number: '',
      specializations: [],
      coverage_states: [],
      coverage_type: 'pan_india',
      onboarding_status: 'pending',
      tat_days: 7,
      bank_name: '',
      bank_account: '',
      bank_ifsc: '',
      bank_branch: '',
      agreement_file: null,
      agreement_validity: '',
      send_invite: true, // Send portal invite by default
      tat_penalties: [
        { period_start: 6, period_end: 10, penalty_percent: 5 },
        { period_start: 11, period_end: 15, penalty_percent: 10 },
        { period_start: 16, period_end: 20, penalty_percent: 15 },
        { period_start: 21, period_end: null, penalty_percent: 20 }
      ]
    });
  };

  // Reset vendor password
  const handleResetVendorPassword = async (vendor) => {
    if (!window.confirm(`Reset password for ${vendor.name}? A new password will be generated and sent to ${vendor.email}`)) {
      return;
    }
    try {
      const response = await axios.post(`/subvendors/${vendor.id}/reset-password`);
      toast.success(`Password reset! New password sent to ${vendor.email}`);
      if (response.data.temp_password) {
        // Show temp password in case email fails
        toast.info(`Temporary password: ${response.data.temp_password}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  // Resend vendor invite
  const handleResendVendorInvite = async (vendor) => {
    try {
      await axios.post(`/subvendors/${vendor.id}/send-invite`);
      toast.success(`Invite sent to ${vendor.email}`);
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  const openEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorFormData({
      name: vendor.name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      contact_person: vendor.contact_person || '',
      gst_number: vendor.gst_number || '',
      pan_number: vendor.pan_number || '',
      specializations: vendor.specializations || [],
      coverage_states: vendor.coverage_states || [],
      coverage_type: vendor.coverage_type || 'pan_india',
      onboarding_status: vendor.onboarding_status || 'pending',
      tat_days: vendor.tat_days || 7,
      bank_name: vendor.bank_details?.bank_name || '',
      bank_account: vendor.bank_details?.account_number || '',
      bank_ifsc: vendor.bank_details?.ifsc_code || '',
      bank_branch: vendor.bank_details?.branch || '',
      agreement_file: vendor.agreement_file || null,
      agreement_validity: vendor.agreement_validity || '',
      check_pricing: vendor.check_pricing || [],
      check_tat: vendor.check_tat || {},
      tat_penalties: vendor.tat_penalties || [
        { period_start: 6, period_end: 10, penalty_percent: 5 },
        { period_start: 11, period_end: 15, penalty_percent: 10 },
        { period_start: 16, period_end: 20, penalty_percent: 15 },
        { period_start: 21, period_end: null, penalty_percent: 20 }
      ]
    });
    setShowVendorDialog(true);
  };

  const handleCreateVendor = async () => {
    // Validate mandatory fields
    if (!vendorFormData.specializations || vendorFormData.specializations.length === 0) {
      toast.error('Specializations (Check Types) are mandatory');
      return;
    }
    if (!vendorFormData.check_pricing || vendorFormData.check_pricing.length === 0) {
      toast.error('Pricing Configuration is mandatory. Add at least one pricing entry.');
      return;
    }
    try {
      const vendorData = {
        ...vendorFormData,
        bank_details: {
          bank_name: vendorFormData.bank_name,
          account_number: vendorFormData.bank_account_number,
          account_holder: vendorFormData.bank_account_holder,
          ifsc_code: vendorFormData.bank_ifsc,
        },
        send_invite: vendorFormData.send_invite
      };
      const response = await axios.post('/subvendors', vendorData);
      toast.success('Vendor created successfully');
      if (vendorFormData.send_invite && response.data?.generated_password) {
        toast.info(`Portal credentials sent to ${vendorFormData.email}`, { duration: 5000 });
      }
      setShowVendorDialog(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      console.error('Vendor creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create vendor');
    }
  };

  const handleUpdateVendor = async () => {
    if (!editingVendor) return;
    try {
      const vendorData = {
        ...vendorFormData,
        bank_details: {
          bank_name: vendorFormData.bank_name,
          account_number: vendorFormData.bank_account_number,
          account_holder: vendorFormData.bank_account_holder,
          ifsc_code: vendorFormData.bank_ifsc,
        },
        // Preserve pricing - use form data if set, else keep existing
        check_pricing: vendorFormData.check_pricing?.length > 0 
          ? vendorFormData.check_pricing 
          : editingVendor.check_pricing || [],
        check_tat: vendorFormData.check_tat && Object.keys(vendorFormData.check_tat).length > 0
          ? vendorFormData.check_tat
          : editingVendor.check_tat || {},
      };
      await axios.put(`/subvendors/${editingVendor.id}`, vendorData);
      toast.success('Vendor updated successfully');
      setShowVendorDialog(false);
      setEditingVendor(null);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      console.error('Vendor update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update vendor');
    }
  };

  const openContactsDialog = async (vendor) => {
    setSelectedVendorForContacts(vendor);
    setVendorContacts(vendor.additional_contacts || []);
    setShowContactsDialog(true);
  };

  const openTatDialog = (vendor) => {
    setSelectedVendorForTat(vendor);
    setVendorTatConfig(vendor.check_tat || {});
    setShowTatDialog(true);
  };

  const handleAddContact = () => {
    if (!newContact.name || !newContact.email) {
      toast.error('Name and email are required');
      return;
    }
    setVendorContacts([...vendorContacts, { ...newContact }]);
    setNewContact({ name: '', email: '', phone: '', designation: '', department: '', cc_emails: true, cc_notifications: true });
  };

  // Toggle CC flags on existing contact rows. The buttons referenced this
  // handler but it was never defined — clicking the checkbox threw a silent
  // ReferenceError, so user CC choices were also lost on prod.
  const handleToggleContactCC = (index, field, checked) => {
    setVendorContacts(prev => prev.map((c, i) => (i === index ? { ...c, [field]: !!checked } : c)));
  };

  const handleRemoveContact = (index) => {
    setVendorContacts(vendorContacts.filter((_, i) => i !== index));
  };

  const handleSaveContacts = async () => {
    if (!selectedVendorForContacts) return;
    try {
      await axios.put(`/subvendors/${selectedVendorForContacts.id}`, {
        additional_contacts: vendorContacts
      });
      toast.success('Contacts updated successfully');
      setShowContactsDialog(false);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to save contacts');
    }
  };

  const handleSaveTatConfig = async () => {
    if (!selectedVendorForTat) return;
    try {
      await axios.put(`/subvendors/${selectedVendorForTat.id}`, {
        check_tat: vendorTatConfig
      });
      toast.success('TAT configuration saved');
      setShowTatDialog(false);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to save TAT config');
    }
  };

  const handleDeactivateVendor = async (vendor) => {
    if (!window.confirm('Are you sure you want to deactivate this vendor?')) return;
    try {
      await axios.put(`/subvendors/${vendor.id}`, { is_active: false });
      toast.success('Vendor deactivated');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to deactivate vendor');
    }
  };

  const handleApproveVendor = async (vendor) => {
    try {
      await axios.put(`/subvendors/${vendor.id}`, { onboarding_status: 'active' });
      toast.success('Vendor approved and activated');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to approve vendor');
    }
  };

  const handleOnboardVendor = async (vendorId, newStatus) => {
    try {
      await axios.post(`/subvendors/${vendorId}/onboard`, { status: newStatus });
      const statusLabels = {
        'verified': 'Vendor marked as verified',
        'active': 'Vendor activated',
        'suspended': 'Vendor suspended',
        'pending': 'Vendor set to pending'
      };
      toast.success(statusLabels[newStatus] || 'Vendor status updated');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const fetchAutoAssignSettings = async () => {
    try {
      const res = await axios.get('/operations/auto-assign-settings');
      setAutoAssignSettings(res.data);
      setAutoAssignEnabled(res.data.auto_assign_enabled || false);
    } catch (error) {
      console.error('Failed to load auto-assign settings:', error);
    }
  };

  const fetchVerificationModeSettings = async () => {
    try {
      const res = await axios.get('/operations/verification-mode-settings');
      setVerificationModeSettings(res.data);
    } catch (error) {
      console.error('Failed to load verification mode settings:', error);
    }
  };

  const saveVerificationModeSettings = async (newSettings) => {
    try {
      await axios.put('/operations/verification-mode-settings', newSettings);
      setVerificationModeSettings(newSettings);
      toast.success('Verification mode settings saved');
    } catch (error) {
      toast.error('Failed to save verification settings');
    }
  };

  const saveAutoAssignSettings = async (newSettings) => {
    try {
      await axios.put('/operations/auto-assign-settings', newSettings);
      setAutoAssignSettings(newSettings);
      setAutoAssignEnabled(newSettings.auto_assign_enabled);
      toast.success('Auto-assign settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const toggleAutoAssign = async (enabled) => {
    const newSettings = { ...autoAssignSettings, auto_assign_enabled: enabled };
    await saveAutoAssignSettings(newSettings);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.clientId && filters.clientId !== 'all') params.append('client_id', filters.clientId);
      if (filters.checkType && filters.checkType !== 'all') params.append('check_type', filters.checkType);
      if (filters.tatStatus && filters.tatStatus !== 'all') params.append('tat_status', filters.tatStatus);
      // Note: status filter is applied client-side via getFilteredChecks() for verification status
      
      // Date filters - Received (QC Approved), Assigned (Sent), Completed (Verified)
      if (filters.receivedFrom) params.append('received_from', filters.receivedFrom);
      if (filters.receivedTo) params.append('received_to', filters.receivedTo);
      if (filters.assignedFrom) params.append('assigned_from', filters.assignedFrom);
      if (filters.assignedTo) params.append('assigned_to', filters.assignedTo);
      if (filters.completedFrom) params.append('completed_from', filters.completedFrom);
      if (filters.completedTo) params.append('completed_to', filters.completedTo);
      if (filters.search) params.append('search', filters.search);

      const [dashboardRes, insuffRes, workloadRes] = await Promise.all([
        axios.get(`/operations/dashboard?${params.toString()}`),
        axios.get('/operations/insufficiencies'),
        axios.get('/operations/workload'),
      ]);
      
      const allChecks = dashboardRes.data.checks || [];
      setChecks(allChecks);
      setStats(dashboardRes.data.stats || {});
      setInsufficiencies(insuffRes.data || []);
      
      // Filter returned checks
      setReturnedChecks(allChecks.filter(c => c.status === 'returned'));
      
      // Set workload data from API
      const workload = workloadRes.data || {};
      setWorkloadData(workload);
      
      // Note: Agents are fetched separately via fetchAgentsAndVendors() to get proper user data
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsData = async () => {
    try {
      const [teamsRes, teamWorkloadRes] = await Promise.all([
        axios.get('/verification-teams'),
        axios.get('/operations/team-workload'),
      ]);
      
      setVerificationTeams(teamsRes.data || []);
      
      // Separate vendor and internal team checks
      const teamWorkload = teamWorkloadRes.data || [];
      const vendorTeam = teamWorkload.find(t => t.team_type === 'vendor') || {};
      const internalTeam = teamWorkload.find(t => t.team_type === 'internal') || {};
      
      setVendorTeamChecks(vendorTeam);
      setInternalTeamChecks(internalTeam);
    } catch (error) {
      console.error('Failed to load teams data:', error);
    }
  };

  const fetchAgentsAndVendors = async () => {
    try {
      const [agentsRes, vendorsRes] = await Promise.all([
        axios.get('/users?role=verification_team,inhouse_verification,vendor_management&include_inactive=true'),
        axios.get('/subvendors'),
      ]);
      
      const relevantAgents = (agentsRes.data || []).filter(agent => {
        const role = agent.role || '';
        
        // Filter to only active agents
        if (agent.is_active === false) return false;
        
        // Include agents from relevant roles
        return role === 'inhouse_verification' ||
               role === 'vendor_management' ||
               role === 'verification_team';
      });
      
      // Deduplicate by email
      const uniqueAgents = [];
      const seenEmails = new Set();
      for (const agent of relevantAgents) {
        if (!seenEmails.has(agent.email)) {
          seenEmails.add(agent.email);
          uniqueAgents.push(agent);
        }
      }
      
      setAgents(uniqueAgents);
      setVendors((vendorsRes.data || []).filter(v => v.onboarding_status !== 'suspended' && v.is_active !== false).sort((a, b) => (a.vendor_name || a.name || '').localeCompare(b.vendor_name || b.name || '')));
    } catch (error) {
      console.error('Failed to load agents/vendors');
    }
  };

  // Alias for backward compatibility
  const fetchVendors = fetchAgentsAndVendors;

  const fetchClients = async () => {
    try {
      const response = await axios.get('/clients');
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchDatabases = async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const [companiesRes, institutionsRes] = await Promise.all([
        axios.get(`/company-templates${params}`),
        axios.get(`/institution-templates${params}`),
      ]);
      setCompanyTemplates(companiesRes.data || []);
      setInstitutionTemplates(institutionsRes.data || []);
    } catch (error) {
      console.error('Failed to load databases:', error);
    }
  };

  // Load databases when tab changes to company-db or university-db
  useEffect(() => {
    if (activeTab === 'company-db' || activeTab === 'university-db') {
      fetchDatabases();
    }
  }, [activeTab]);

  const handleSelectCheck = (checkId, caseId, rowId) => {
    const key = rowId || `${caseId}:${checkId}`;
    setSelectedChecks(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    const filteredChecks = getFilteredChecks();
    // Check if all filtered checks are currently selected
    const filteredKeys = filteredChecks.map(c => c.row_id || `${c.case_id}:${c.check_id}`);
    const allFilteredSelected = filteredKeys.length > 0 && filteredKeys.every(key => selectedChecks.includes(key));
    
    if (allFilteredSelected) {
      // Deselect only the filtered checks (keep any other selections)
      setSelectedChecks(prev => prev.filter(key => !filteredKeys.includes(key)));
    } else {
      // Select all filtered checks (merge with existing selections)
      setSelectedChecks(prev => {
        const newSelection = new Set(prev);
        filteredKeys.forEach(key => newSelection.add(key));
        return Array.from(newSelection);
      });
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedAssignee) {
      toast.error(`Please select a ${assignType}`);
      return;
    }

    try {
      const endpoint = assignType === 'agent' ? '/operations/assign-agent' : '/operations/assign-vendor';
      const assigneeKey = assignType === 'agent' ? 'agent_id' : 'vendor_id';

      let successCount = 0;
      for (const key of selectedChecks) {
        // Handle both formats: "case_id:check_id" and row_id
        let caseId, checkId;
        if (key.includes(':')) {
          [caseId, checkId] = key.split(':');
        } else {
          // For row_id format, find the check from the loaded data
          const check = checks.find(c => c.row_id === key);
          if (check) {
            caseId = check.case_id;
            checkId = check.check_id;
          } else {
            // Skip if we can't find the check
            continue;
          }
        }
        
        if (!caseId || !checkId) continue;
        
        // Find the check to get additional details
        const checkData = checks.find(c => c.check_id === checkId || c.row_id === key);
        
        try {
          await axios.post(endpoint, {
            case_id: caseId,
            check_id: checkId,
            check_type: checkData?.check_type,
            parent_check_id: checkData?.parent_check_id,
            [assigneeKey]: selectedAssignee,
          });
          successCount++;
        } catch (e) {
          console.error(`Failed to assign ${checkId}:`, e);
        }
      }

      toast.success(`Successfully assigned ${successCount} check(s)`);
      setShowAssignDialog(false);
      setSelectedChecks([]);
      setSelectedAssignee('');
      setIsReassign(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign checks');
    }
  };

  const handleAutoAssign = async () => {
    try {
      const response = await axios.post('/operations/auto-assign', {
        check_ids: selectedChecks.map(k => {
          // Handle both formats: "case_id:check_id" and row_id
          if (k.includes(':')) {
            const [caseId, checkId] = k.split(':');
            return { case_id: caseId, check_id: checkId };
          } else {
            // For row_id format, find the check from the loaded data
            const check = checks.find(c => c.row_id === k);
            if (check) {
              return { case_id: check.case_id, check_id: check.check_id };
            }
            // Fallback: assume underscore separator between check_id and subcheck_id
            const checkId = k.split('_')[0];
            const matchingCheck = checks.find(c => c.check_id === checkId);
            return { 
              case_id: matchingCheck?.case_id || '', 
              check_id: checkId 
            };
          }
        }).filter(c => c.case_id && c.check_id),
      });
      toast.success(`Auto-assigned ${response.data.assigned_count} check(s)`);
      setSelectedChecks([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to auto-assign checks');
    }
  };

  const handleRaiseInsufficiency = async () => {
    if (!insuffData.reason || !insuffData.description) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post('/operations/raise-insufficiency', {
        case_id: selectedCheck.case_id,
        check_id: selectedCheck.check_id,
        reason: insuffData.reason,
        description: insuffData.description,
        target_team: insuffData.targetTeam,
        requested_documents: insuffData.requestedDocuments,
        requested_info: insuffData.requestedInfo,
      });
      
      toast.success('Insufficiency raised successfully');
      setShowRaiseInsuff(false);
      setSelectedCheck(null);
      setInsuffData({ reason: '', description: '', targetTeam: 'qc', requestedDocuments: [], requestedInfo: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to raise insufficiency');
    }
  };

  const handleReinitiate = async () => {
    if (!reinitiateData.notes) {
      toast.error('Please provide notes');
      return;
    }

    try {
      await axios.post(`/operations/insufficiency/${selectedInsuff.id}/reinitiate`, {
        target: reinitiateData.target,
        notes: reinitiateData.notes,
      });
      
      toast.success(`Re-initiated to ${reinitiateData.target.toUpperCase()}`);
      setShowReinitiate(false);
      setSelectedInsuff(null);
      setReinitiateData({ target: 'hr', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to re-initiate');
    }
  };

  const openRaiseInsuff = (check) => {
    setSelectedCheck(check);
    setShowRaiseInsuff(true);
  };

  const openReinitiate = (insuff) => {
    setSelectedInsuff(insuff);
    setShowReinitiate(true);
  };

  // Auto-Initiate Functions
  const openAutoInitiate = async (check) => {
    setAutoInitiateCheck(check);
    setShowAutoInitiate(true);
    setAutoInitiateLoading(true);
    setAutoInitiateResult(null);
    
    try {
      // Check if this sub-check can be auto-initiated
      const res = await axios.post('/auto-initiate/subcheck', {
        case_id: check.case_id,
        check_id: check.check_id,
        subcheck_index: check.subcheck_index || 0,
        send_email: false  // Just check, don't send yet
      });
      setAutoInitiateResult(res.data);
    } catch (error) {
      console.error('Failed to check auto-initiate:', error);
      setAutoInitiateResult({
        can_auto_initiate: false,
        message: 'Failed to check auto-initiate eligibility'
      });
    } finally {
      setAutoInitiateLoading(false);
    }
  };

  const handleAutoInitiateEmail = async () => {
    if (!autoInitiateCheck || !autoInitiateResult?.can_auto_initiate) return;
    
    setAutoInitiateLoading(true);
    try {
      const res = await axios.post('/auto-initiate/subcheck', {
        case_id: autoInitiateCheck.case_id,
        check_id: autoInitiateCheck.check_id,
        subcheck_index: autoInitiateCheck.subcheck_index || 0,
        send_email: true
      });
      
      if (res.data.email_sent) {
        toast.success(`Verification email sent to ${res.data.target_email}`);
      } else {
        toast.info(res.data.message || 'Auto-initiate completed');
      }
      
      setShowAutoInitiate(false);
      setAutoInitiateCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to send verification email');
      console.error('Auto-initiate email error:', error);
    } finally {
      setAutoInitiateLoading(false);
    }
  };

  const openAssignDialog = (type, reassign = false) => {
    if (selectedChecks.length === 0) {
      toast.error('Please select at least one check');
      return;
    }
    setAssignType(type);
    setSelectedAssignee('');
    setIsReassign(reassign);
    setShowAssignDialog(true);
  };

  // Handler to save new contact to database (after agent confirmation)
  const handleSaveContactToDatabase = async () => {
    if (!autoInitiateCheck || !manualContactEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setAutoInitiateLoading(true);
    try {
      const checkType = autoInitiateCheck.check_type;
      const detail = autoInitiateResult?.subcheck_detail || {};
      
      const dbType = checkType === 'employment_check' ? 'company' : 
                     checkType === 'education_check' ? 'institution' : null;
      
      if (!dbType) {
        toast.error('Cannot determine database type for this check');
        return;
      }
      
      const name = checkType === 'employment_check' 
        ? (detail.company_name || autoInitiateCheck.subcheck_company)
        : (detail.university_name || autoInitiateCheck.subcheck_institution);
      
      if (!name) {
        toast.error('Cannot determine company/institution name');
        return;
      }
      
      const res = await axios.post('/database/update-contact', {
        type: dbType,
        name: name,
        email: manualContactEmail,
        contact_name: manualContactName,
        enable_auto_initiate: saveToDatabase
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowAutoInitiate(false);
        setManualContactEmail('');
        setManualContactName('');
        fetchData();
      } else {
        toast.error('Failed to save contact');
      }
    } catch (error) {
      toast.error('Failed to save contact to database');
      console.error('Save contact error:', error);
    } finally {
      setAutoInitiateLoading(false);
    }
  };

  const openCheckDetails = async (check) => {
    setSelectedCheck(check);
    setShowCheckDetails(true);
    setLoadingDocs(true);
    setCheckDocuments([]);
    setPreviewDoc(null);
    setCheckDetailData(null);
    setSelectedSubcheck(null);
    
    try {
      const checkId = check.check_id || check.id;
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/details`);
      const docs = res.data.documents || [];
      setCheckDocuments(docs);
      setCheckDetailData(res.data);
      
      // Auto-select first document for preview if available
      if (docs.length > 0) {
        setPreviewDoc(docs[0]);
      }
    } catch (error) {
      console.error('Failed to load check documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Helper function to get subchecks based on check type
  const getSubchecks = (checkType, employee) => {
    if (!employee) return [];
    
    switch (checkType) {
      case 'address_check':
        const addresses = [];
        if (employee.present_address) addresses.push({ type: 'Present Address', ...employee.present_address });
        if (employee.permanent_address) addresses.push({ type: 'Permanent Address', ...employee.permanent_address });
        if (employee.additional_addresses) addresses.push(...employee.additional_addresses.map((a, i) => ({ type: `Additional Address ${i + 1}`, ...a })));
        return addresses;
      case 'education_check':
        return (employee.education_entries || []).map((e, i) => ({ 
          type: e.education_type === 'highest' ? 'Highest Education' : `Education ${i + 1}`, 
          ...e 
        }));
      case 'employment_check':
        return (employee.employment_entries || []).map((e, i) => ({ 
          type: e.employment_type === 'current' ? 'Current Employment' : `Previous Employment ${i}`, 
          ...e 
        }));
      case 'reference_check':
      case 'personal_reference':
        return (employee.personal_references || []).map((r, i) => ({ type: `Reference ${i + 1}`, ...r }));
      case 'professional_reference':
        return (employee.professional_references || []).map((r, i) => ({ type: `Prof. Reference ${i + 1}`, ...r }));
      default:
        return [];
    }
  };

  // Helper function to render subcheck details based on type
  const renderSubcheckDetails = (subcheck, checkType) => {
    if (!subcheck) return null;
    
    if (checkType === 'address_check') {
      return (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Address:</span> {subcheck.complete_address || '-'}</div>
          <div><span className="text-gray-500">Landmark:</span> {subcheck.landmark || '-'}</div>
          <div><span className="text-gray-500">City:</span> {subcheck.city || '-'}</div>
          <div><span className="text-gray-500">State:</span> {subcheck.state || '-'}</div>
          <div><span className="text-gray-500">Pincode:</span> {subcheck.pincode || '-'}</div>
          {subcheck.address_proof_url && (
            <div className="col-span-2">
              <Button variant="outline" size="sm" onClick={() => window.open(subcheck.address_proof_url, '_blank')}>
                <FileText className="w-4 h-4 mr-1" />View Address Proof
              </Button>
            </div>
          )}
        </div>
      );
    }
    
    if (checkType === 'education_check') {
      return (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Name on Doc:</span> {subcheck.name_as_per_document || '-'}</div>
          <div><span className="text-gray-500">University:</span> {subcheck.university_name || '-'}</div>
          <div><span className="text-gray-500">College:</span> {subcheck.college || '-'}</div>
          <div><span className="text-gray-500">Course:</span> {subcheck.course || '-'}</div>
          <div><span className="text-gray-500">Specialization:</span> {subcheck.specialization || '-'}</div>
          <div><span className="text-gray-500">Year of Passing:</span> {subcheck.year_of_passing || '-'}</div>
          <div><span className="text-gray-500">Roll No:</span> {subcheck.roll_no || '-'}</div>
          <div className="col-span-2 flex gap-2 flex-wrap">
            {subcheck.degree_certificate_url && (
              <Button variant="outline" size="sm" onClick={() => window.open(subcheck.degree_certificate_url, '_blank')}>
                <FileText className="w-4 h-4 mr-1" />Degree Certificate
              </Button>
            )}
            {subcheck.marks_card_url && (
              <Button variant="outline" size="sm" onClick={() => window.open(subcheck.marks_card_url, '_blank')}>
                <FileText className="w-4 h-4 mr-1" />Marks Card
              </Button>
            )}
          </div>
        </div>
      );
    }
    
    if (checkType === 'employment_check') {
      return (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Company:</span> {subcheck.company_name || '-'}</div>
          <div><span className="text-gray-500">Location:</span> {subcheck.location_address || '-'}</div>
          <div><span className="text-gray-500">Employee ID:</span> {subcheck.employee_id || '-'}</div>
          <div><span className="text-gray-500">Designation:</span> {subcheck.designation || '-'}</div>
          <div><span className="text-gray-500">From:</span> {subcheck.employed_from || '-'}</div>
          <div><span className="text-gray-500">To:</span> {subcheck.employed_to || 'Present'}</div>
          <div><span className="text-gray-500">Salary:</span> {subcheck.salary_per_month || '-'}</div>
          <div><span className="text-gray-500">UAN/PF:</span> {subcheck.uan_pf_number || '-'}</div>
          <div className="col-span-2 border-t pt-2 mt-2">
            <p className="font-medium mb-1">Reporting Manager</p>
            <div className="grid grid-cols-3 gap-2">
              <div><span className="text-gray-500">Name:</span> {subcheck.rm_name || '-'}</div>
              <div><span className="text-gray-500">Email:</span> {subcheck.rm_email || '-'}</div>
              <div><span className="text-gray-500">Phone:</span> {subcheck.rm_contact_number || '-'}</div>
            </div>
          </div>
          <div className="col-span-2 flex gap-2 flex-wrap">
            {subcheck.relieving_letter_url && (
              <Button variant="outline" size="sm" onClick={() => window.open(subcheck.relieving_letter_url, '_blank')}>
                <FileText className="w-4 h-4 mr-1" />Relieving Letter
              </Button>
            )}
            {subcheck.latest_payslip_url && (
              <Button variant="outline" size="sm" onClick={() => window.open(subcheck.latest_payslip_url, '_blank')}>
                <FileText className="w-4 h-4 mr-1" />Payslip
              </Button>
            )}
          </div>
        </div>
      );
    }
    
    if (checkType === 'reference_check' || checkType === 'personal_reference' || checkType === 'professional_reference') {
      return (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Name:</span> {subcheck.name || '-'}</div>
          <div><span className="text-gray-500">Relation:</span> {subcheck.relation || subcheck.designation || '-'}</div>
          <div><span className="text-gray-500">Phone:</span> {subcheck.phone || subcheck.contact_number || '-'}</div>
          <div><span className="text-gray-500">Email:</span> {subcheck.email || '-'}</div>
          {subcheck.company && <div><span className="text-gray-500">Company:</span> {subcheck.company || '-'}</div>}
          <div className="col-span-2"><span className="text-gray-500">Address:</span> {subcheck.address || '-'}</div>
        </div>
      );
    }
    
    return <p className="text-sm text-gray-500">No additional details available for this check type.</p>;
  };

  // Filter checks by verification progress
  const notInitiatedChecks = checks.filter(c => 
    (!c.verification_progress || c.verification_progress === 'not_initiated') && !c.has_insufficiency
  );
  
  // Not Assigned to Agent - checks without assigned_agent_name (exclude mirrored permanent checks)
  const notAssignedChecks = checks.filter(c => 
    !c.assigned_agent_name && c.verification_progress !== 'completed' && c.status !== 'verified' &&
    !(c.permanent_same_as_present && c.sub_option === 'permanent')
  );
  
  const inProgressChecks = checks.filter(c => 
    (c.verification_progress === 'in_progress' || c.verification_progress === 'initiated' || c.status === 'in_progress') && !c.has_insufficiency
  );
  
  const completedChecks = checks.filter(c => 
    c.verification_progress === 'completed' || c.status === 'verified' || c.status === 'clear' || c.status === 'failed' || c.status === 'completed'
  );
  
  const insufficiencyChecks = checks.filter(c => 
    c.has_insufficiency || c.verification_progress === 'insufficiency'
  );

  const mirroredAddressChecks = checks.filter(c => 
    c.permanent_same_as_present && c.sub_option === 'permanent'
  );

  // Drill-down data computations
  const getDrillDownData = () => {
    switch (drillDownType) {
      case 'not_initiated':
        return {
          title: 'Not Initiated Checks',
          color: 'orange',
          data: checks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated'),
          columns: ['employee', 'client', 'check_type', 'status', 'created_at']
        };
      case 'not_assigned':
        return {
          title: 'Not Assigned to Agent',
          color: 'rose',
          data: checks.filter(c => !c.assigned_agent_name && c.verification_progress !== 'completed' && c.status !== 'verified' && !(c.permanent_same_as_present && c.sub_option === 'permanent')),
          columns: ['employee', 'client', 'check_type', 'status', 'created_at', 'received_at']
        };
      case 'newly_added':
        // Checks added in last 24 hours
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        return {
          title: 'Newly Added (Last 24 Hours)',
          color: 'blue',
          data: checks.filter(c => c.created_at && c.created_at >= oneDayAgo),
          columns: ['employee', 'client', 'check_type', 'status', 'created_at']
        };
      case 'in_progress':
        return {
          title: 'In Progress Checks',
          color: 'cyan',
          data: checks.filter(c => c.verification_progress === 'in_progress' || c.verification_progress === 'initiated' || c.status === 'in_progress'),
          columns: ['employee', 'client', 'check_type', 'assigned_to', 'assigned_at', 'tat']
        };
      case 'within_tat':
        return {
          title: 'Within TAT',
          color: 'green',
          data: checks.filter(c => c.is_within_tat && c.verification_progress !== 'completed'),
          columns: ['employee', 'client', 'check_type', 'status', 'tat_days', 'days_elapsed']
        };
      case 'out_of_tat':
        return {
          title: 'Out of TAT',
          color: 'red',
          data: checks.filter(c => !c.is_within_tat && c.verification_progress !== 'completed'),
          columns: ['employee', 'client', 'check_type', 'status', 'tat_days', 'days_elapsed', 'assigned_to']
        };
      case 'completed':
        return {
          title: 'Completed Checks',
          color: 'emerald',
          data: checks.filter(c => c.verification_progress === 'completed' || c.status === 'verified' || c.status === 'clear' || c.status === 'failed'),
          columns: ['employee', 'client', 'check_type', 'status', 'completed_at', 'assigned_to']
        };
      case 'insufficiency':
        return {
          title: 'Insufficiency Checks',
          color: 'red',
          data: checks.filter(c => c.has_insufficiency || c.verification_progress === 'insufficiency'),
          columns: ['employee', 'client', 'check_type', 'status', 'assigned_to']
        };
      case 'insuff_raised':
        return {
          title: 'Insufficiencies Raised',
          color: 'amber',
          data: insufficiencies.filter(i => i.status === 'open' || i.status === 'pending'),
          columns: ['case_id', 'check_type', 'reason', 'raised_by', 'raised_at', 'status'],
          isInsuff: true
        };
      case 'insuff_cleared':
        return {
          title: 'Insufficiencies Cleared',
          color: 'teal',
          data: insufficiencies.filter(i => i.status === 'cleared' || i.status === 'resolved'),
          columns: ['case_id', 'check_type', 'reason', 'raised_by', 'cleared_at', 'status'],
          isInsuff: true
        };
      case 'mirrored':
        return {
          title: 'Present & Permanent Address Same (Mirrored)',
          color: 'blue',
          data: checks.filter(c => c.permanent_same_as_present && c.sub_option === 'permanent'),
          columns: ['employee', 'client', 'check_type', 'status', 'assigned_to']
        };
      default:
        return { title: '', color: 'gray', data: [], columns: [] };
    }
  };

  const handleStatCardClick = (type) => {
    setDrillDownType(type);
    setShowDrillDown(true);
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Row 1 - Main Stats */}
      <div className="grid grid-cols-6 gap-4">
        <Card 
          className="border-orange-200 bg-orange-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('not_initiated')}
          data-testid="stat-not-initiated"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-900">{notInitiatedChecks.length}</p>
                <p className="text-sm text-orange-700">Not Initiated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-rose-200 bg-rose-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('not_assigned')}
          data-testid="stat-not-assigned"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <UserPlus className="w-8 h-8 text-rose-600" />
              <div>
                <p className="text-2xl font-bold text-rose-900">{notAssignedChecks.length}</p>
                <p className="text-sm text-rose-700">Not Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('newly_added')}
          data-testid="stat-newly-added"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <PlusCircle className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-900">{stats.newly_added || 0}</p>
                <p className="text-sm text-blue-700">Newly Added</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-cyan-200 bg-cyan-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('in_progress')}
          data-testid="stat-in-progress"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-cyan-600" />
              <div>
                <p className="text-2xl font-bold text-cyan-900">{inProgressChecks.length}</p>
                <p className="text-sm text-cyan-700">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-green-200 bg-green-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('within_tat')}
          data-testid="stat-within-tat"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-900">{stats.within_tat || 0}</p>
                <p className="text-sm text-green-700">Within TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-red-200 bg-red-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('out_of_tat')}
          data-testid="stat-out-of-tat"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-900">{stats.out_of_tat || 0}</p>
                <p className="text-sm text-red-700">Out of TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Row 2 - Completed & Insufficiency Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card 
          className="border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('completed')}
          data-testid="stat-completed"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-900">{completedChecks.length}</p>
                <p className="text-sm text-emerald-700">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-red-200 bg-red-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('insufficiency')}
          data-testid="stat-insufficiency"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-900">{insufficiencyChecks.length}</p>
                <p className="text-sm text-red-700">Insufficiency</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="border-teal-200 bg-teal-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
          onClick={() => handleStatCardClick('insuff_cleared')}
          data-testid="stat-insuff-cleared"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-2xl font-bold text-teal-900">{stats.insufficiencies_cleared || 0}</p>
                <p className="text-sm text-teal-700">Insuff Cleared</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {mirroredAddressChecks.length > 0 && (
          <Card 
            className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md hover:scale-102 transition-all"
            onClick={() => handleStatCardClick('mirrored')}
            data-testid="stat-mirrored-address"
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Copy className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-900">{mirroredAddressChecks.length}</p>
                  <p className="text-sm text-blue-700">Address Same</p>
                  <p className="text-[10px] text-blue-500">Present = Permanent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-8 h-8 text-indigo-600" />
                <div>
                  <p className="text-sm text-indigo-700">Auto-Assign</p>
                </div>
              </div>
              <Switch
                checked={autoAssignEnabled}
                onCheckedChange={toggleAutoAssign}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-2 border-blue-100">
        <CardContent className="pt-4">
          {/* Quick Filters Row - Buttons like Agent Dashboard */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Label className="text-sm font-medium mr-2">Quick Filters:</Label>
            {['latest', 'today', 'this_week', 'this_month'].map(type => (
              <Button 
                key={type} 
                size="sm" 
                variant={filters.dateFilterType === type ? 'default' : 'outline'}
                onClick={() => handleQuickFilterChange(type)} 
                className={filters.dateFilterType === type ? 'bg-indigo-600' : ''}
              >
                {type === 'latest' && <RefreshCw className="w-3 h-3 mr-1" />}
                {type === 'today' && <Calendar className="w-3 h-3 mr-1" />}
                {type === 'this_week' && <Clock className="w-3 h-3 mr-1" />}
                {type === 'this_month' && <BarChart3 className="w-3 h-3 mr-1" />}
                {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Button>
            ))}
          </div>
          
          {/* All Filters + Search + Clear in one row */}
          <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
            <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Check Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CHECK_TYPES.map(ct => (
                  <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
              <SelectTrigger>
                <SelectValue placeholder="TAT" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All TAT</SelectItem>
                <SelectItem value="within_tat">Within TAT</SelectItem>
                <SelectItem value="out_of_tat">Out of TAT</SelectItem>
              </SelectContent>
            </Select>

            {/* Team Type Filter */}
            <Select value={filters.teamType} onValueChange={(v) => setFilters({...filters, teamType: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="internal">Internal Team</SelectItem>
                <SelectItem value="vendor">Vendor Team</SelectItem>
              </SelectContent>
            </Select>

            {/* Vendor Filter */}
            <Select value={filters.vendorId} onValueChange={(v) => setFilters({...filters, vendorId: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {vendors.map(vendor => (
                  <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search..." 
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </div>

            {/* Clear Button */}
            <Button 
              variant="outline" 
              onClick={() => {
                setFilters({
                  clientId: 'all',
                  checkType: 'all',
                  tatStatus: 'all',
                  status: 'all',
                  teamType: 'all',
                  agentId: 'all',
                  vendorId: 'all',
                  search: '',
                  dateFilterType: 'latest',
                  receivedFrom: '',
                  receivedTo: '',
                  assignedFrom: '',
                  assignedTo: '',
                  completedFrom: '',
                  completedTo: '',
                });
              }}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      {selectedChecks.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="font-medium text-blue-900">{selectedChecks.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => openAssignDialog('agent')}>
            <UserPlus className="w-4 h-4 mr-2" />
            Assign to Agent
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAssignDialog('vendor')}>
            <Building2 className="w-4 h-4 mr-2" />
            Assign to Vendor
          </Button>
          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => openAssignDialog('agent', true)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reassign Selected
          </Button>
          {autoAssignEnabled && (
            <Button size="sm" className="bg-indigo-600" onClick={handleAutoAssign}>
              <Zap className="w-4 h-4 mr-2" />
              Auto Assign
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelectedChecks([])}>
            Clear
          </Button>
        </div>
      )}

      {/* Checks Table */}
      <Card className="border-2 border-emerald-100">
        <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-emerald-800 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Checks Overview
            </CardTitle>
            <div className="flex items-center gap-4">
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Show:</span>
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {getFilteredChecks().length} of {checks.length} records
              </Badge>
              <Button onClick={() => setShowBulkDocUpload(true)} variant="outline" size="sm" className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100">
                <FolderUp className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
              <Button onClick={fetchData} variant="ghost" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                  <TableHead className="w-10 text-white">
                    <Checkbox 
                      checked={(() => {
                        const filteredChecks = getFilteredChecks();
                        if (filteredChecks.length === 0) return false;
                        const filteredKeys = filteredChecks.map(c => c.row_id || `${c.case_id}:${c.check_id}`);
                        return filteredKeys.every(key => selectedChecks.includes(key));
                      })()}
                      onCheckedChange={handleSelectAll}
                      className="border-white"
                    />
                  </TableHead>
                  <TableHead className="w-28 text-white font-semibold">Case ID / Check ID</TableHead>
                  <TableHead className="text-white font-semibold">Candidate</TableHead>
                  <TableHead className="text-white font-semibold">Client</TableHead>
                  <TableHead className="text-white font-semibold">Check Type</TableHead>
                  <TableHead className="min-w-[200px] text-white font-semibold">Verification Item</TableHead>
                  <TableHead className="text-white font-semibold">Verification Status</TableHead>
                  <TableHead className="text-white font-semibold">Assigned Agent</TableHead>
                  <TableHead className="text-white font-semibold">Assigned Vendor</TableHead>
                  <TableHead className="text-white font-semibold">
                    <div className="flex flex-col">
                      <span>Agent Assigned</span>
                      <span className="text-[10px] text-white/70 font-normal">(Date)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-white font-semibold">
                    <div className="flex flex-col">
                      <span>Vendor Assigned</span>
                      <span className="text-[10px] text-white/70 font-normal">(Date)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-white font-semibold">
                    <div className="flex flex-col">
                      <span>TAT</span>
                      <span className="text-[10px] text-white/70 font-normal">(Days Elapsed)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center text-white font-semibold">
                    <div className="flex flex-col">
                      <span>QC Approved</span>
                      <span className="text-[10px] text-white/70 font-normal">(Date)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center text-white font-semibold">
                    <div className="flex flex-col">
                      <span>Completed</span>
                      <span className="text-[10px] text-white/70 font-normal">(Verified Date)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-white font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : getFilteredChecks().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-8 text-gray-500">No checks found</TableCell>
                  </TableRow>
                ) : getPaginatedChecks(getFilteredChecks()).map((check) => {
                  const key = check.row_id || `${check.case_id}:${check.check_id}`;
                  return (
                    <TableRow key={key} className="hover:bg-slate-50">
                      <TableCell>
                        <Checkbox 
                          checked={selectedChecks.includes(key)}
                          onCheckedChange={() => handleSelectCheck(check.check_id, check.case_id, check.row_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-mono text-xs font-medium">{check.case_id}</p>
                          <p className="font-mono text-xs text-slate-500">#{check.check_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{check.employee_name}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[120px]">{check.employee_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{check.client_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {check.check_type_display}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {check.has_subcheck ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-slate-700">{check.subcheck_type || '-'}</span>
                            {check.permanent_same_as_present && check.sub_option === 'permanent' && (
                              <Badge data-testid={`same-as-present-badge-${check.row_id}`} className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] w-fit">
                                Same as Present - Mirrored
                              </Badge>
                            )}
                            {check.synced_from_present && (
                              <span className="text-[10px] text-blue-500 italic">Auto-synced from present</span>
                            )}
                            {check.address_updated_after_unlink && (
                              <span data-testid={`address-updated-badge-${check.check_id}`} className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-300 font-semibold">
                                Updated address — re-verify
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Verification Status - show actual progress status with assignment info */}
                        <div className="flex flex-col gap-1">
                          <Badge data-testid={`check-status-badge-${check.check_id || idx}`} className={`text-xs ${
                            check.verification_progress === 'completed' || check.verification_progress === 'verified' ? 'bg-green-100 text-green-800' :
                            check.verification_progress === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            check.verification_progress === 'initiated' ? 'bg-green-100 text-green-800' :
                            check.verification_progress === 'pending_response' ? 'bg-yellow-100 text-yellow-800' :
                            check.verification_progress === 'insufficiency' ? 'bg-red-100 text-red-800' :
                            check.status === 'vendor_assigned' || check.status === 'agent_assigned' || check.status === 'qc_approved' || check.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {check.verification_progress ? 
                              check.verification_progress.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 
                              'Not Initiated'
                            }
                          </Badge>
                          {/* Show verification result below completed badge */}
                          {(check.verification_progress === 'completed' || check.verification_progress === 'verified') && shouldShowResult(check.verification_result) && (
                            <span data-testid={`check-result-label-${check.check_id || idx}`} className={`text-[10px] font-semibold ${getResultTextColor(check.verification_result)}`}>
                              {getResultLabel(check.verification_result)}
                            </span>
                          )}
                          {/* Show assignment sub-status for "Not Initiated" checks */}
                          {(!check.verification_progress || check.verification_progress === 'not_initiated') && (
                            <span className={`text-[10px] font-medium ${
                              check.assigned_agent_name || check.vendor_name 
                                ? 'text-purple-600' 
                                : 'text-rose-500'
                            }`}>
                              {check.assigned_agent_name || check.vendor_name 
                                ? '✓ Assigned - Awaiting Start' 
                                : '⚠ Pending Assignment'
                              }
                            </span>
                          )}
                        </div>
                      </TableCell>
                      {/* Assigned Agent Column */}
                      <TableCell className="text-sm">
                        {check.assigned_agent_name && check.assigned_agent_name !== check.vendor_name ? (
                          <div>
                            <p className="font-medium text-blue-700">{check.assigned_agent_name}</p>
                          </div>
                        ) : check.assigned_to_name && check.assigned_to_name !== check.vendor_name ? (
                          <div>
                            <p className="font-medium text-blue-700">{check.assigned_to_name}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">-</span>
                        )}
                      </TableCell>
                      {/* Assigned Vendor Column */}
                      <TableCell className="text-sm">
                        {check.vendor_name ? (
                          <div>
                            <p className="font-medium text-purple-700">{check.vendor_name}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">-</span>
                        )}
                      </TableCell>
                      {/* Agent Assigned Date Column */}
                      <TableCell className="text-xs text-center">
                        {check.assigned_at ? formatDateDDMMYYYY(check.assigned_at) : '-'}
                      </TableCell>
                      {/* Vendor Assigned Date Column */}
                      <TableCell className="text-xs text-center">
                        {check.vendor_assigned_at ? formatDateDDMMYYYY(check.vendor_assigned_at) : '-'}
                      </TableCell>
                      <TableCell>
                        {check.is_within_tat ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            {check.days_elapsed}/{check.tat_days}d
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 text-xs">
                            {check.days_elapsed}/{check.tat_days}d ⚠️
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        {check.received_at ? formatDateDDMMYYYY(check.received_at) : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-center">
                        {check.completed_at ? formatDateDDMMYYYY(check.completed_at) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => openCheckDetails(check)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          {/* Reassign button for assigned checks */}
                          {(check.assigned_to || check.assigned_vendor || check.vendor_name || check.status === 'vendor_assigned' || check.status === 'agent_assigned') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                setSelectedChecks([check]);
                                setAssignType('agent');
                                setIsReassign(true);
                                setSelectedAssignee('');
                                setShowAssignDialog(true);
                              }}
                              title="Reassign to different agent/vendor"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Reassign
                            </Button>
                          )}
                          {/* Auto-Initiate button for employment/education checks */}
                          {check.has_subcheck && ['employment_check', 'education_check'].includes(check.check_type) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => openAutoInitiate(check)}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Initiate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => openRaiseInsuff(check)}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Insuff
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          {getFilteredChecks().length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
              <div className="text-sm text-gray-600">
                Showing {Math.min((currentPage - 1) * pageSize + 1, getFilteredChecks().length)} to {Math.min(currentPage * pageSize, getFilteredChecks().length)} of {getFilteredChecks().length} filtered records
                {getFilteredChecks().length !== checks.length && ` (${checks.length} total)`}
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">
                  Page {currentPage} of {Math.ceil(getFilteredChecks().length / pageSize) || 1}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(getFilteredChecks().length / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(getFilteredChecks().length / pageSize)}
                >
                  Next
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(Math.ceil(getFilteredChecks().length / pageSize))}
                  disabled={currentPage >= Math.ceil(getFilteredChecks().length / pageSize)}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderWorkload = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Workload Balancing</h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Agent Workload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Agent Workload
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Agent</TableHead>
                <TableHead>Active Checks</TableHead>
                <TableHead>Pending</TableHead>
                <TableHead>In Progress</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Capacity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">No agents found</TableCell>
                </TableRow>
              ) : agents.map(agent => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{agent.name}</p>
                      <p className="text-xs text-gray-500">{agent.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{agent.total_assigned || 0}</TableCell>
                  <TableCell>
                    <Badge className="bg-yellow-100 text-yellow-800">{agent.pending || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-blue-100 text-blue-800">{agent.in_progress || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-800">{agent.completed || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div 
                        className={`h-2 rounded-full ${
                          (agent.utilization || 0) > 80 ? 'bg-red-500' : 
                          (agent.utilization || 0) > 50 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(agent.utilization || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{agent.utilization || 0}% ({agent.total_assigned || 0}/{agent.capacity || 20})</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Vendor Workload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Vendor Workload
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vendor</TableHead>
                <TableHead>Specialization</TableHead>
                <TableHead>Active Checks</TableHead>
                <TableHead>Pending</TableHead>
                <TableHead>In Progress</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Capacity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">No vendors found</TableCell>
                </TableRow>
              ) : vendors.map(vendor => (
                <TableRow key={vendor.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{vendor.name}</p>
                      <p className="text-xs text-gray-500">{vendor.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(vendor.specializations || []).slice(0, 2).map(s => (
                        <Badge key={s} variant="outline" className="text-xs">{s.replace('_', ' ')}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{vendor.total_assigned || 0}</TableCell>
                  <TableCell>
                    <Badge className="bg-yellow-100 text-yellow-800">{vendor.pending || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-blue-100 text-blue-800">{vendor.in_progress || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-800">{vendor.completed || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div 
                        className={`h-2 rounded-full ${
                          (vendor.utilization || 0) > 80 ? 'bg-red-500' : 
                          (vendor.utilization || 0) > 50 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(vendor.utilization || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{vendor.utilization || 0}% ({vendor.total_assigned || 0}/{vendor.capacity || 50})</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const renderInsufficiencies = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Insufficiencies Management</h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="agent" className="w-full">
        <TabsList>
          <TabsTrigger value="agent">Agent Raised ({insufficiencies.filter(i => i.type === 'internal').length})</TabsTrigger>
          <TabsTrigger value="cleared">Cleared ({insufficiencies.filter(i => i.status === 'closed').length})</TabsTrigger>
          <TabsTrigger value="all">All ({insufficiencies.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="agent" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Case ID</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Raised By</TableHead>
                    <TableHead>Raised Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insufficiencies.filter(i => i.type === 'internal').map((insuff) => (
                    <TableRow key={insuff.id}>
                      <TableCell className="font-mono text-xs">{insuff.case_id}</TableCell>
                      <TableCell className="text-sm">{insuff.check_type?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-sm">{insuff.reason?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-sm">{insuff.raised_by_name}</TableCell>
                      <TableCell className="text-xs">
                        {insuff.raised_at ? formatDateDDMMYYYY(insuff.raised_at) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={insuff.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100'}>
                          {insuff.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={() => openReinitiate(insuff)}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Re-initiate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cleared" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Case ID</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Raised Date</TableHead>
                    <TableHead>Cleared Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insufficiencies.filter(i => i.status === 'closed').map((insuff) => (
                    <TableRow key={insuff.id}>
                      <TableCell className="font-mono text-xs">{insuff.case_id}</TableCell>
                      <TableCell className="text-sm">{insuff.check_type?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-sm">{insuff.reason?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-xs">
                        {insuff.raised_at ? formatDateDDMMYYYY(insuff.raised_at) : '-'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {insuff.history?.find(h => h.action === 'closed')?.at 
                          ? new Date(insuff.history.find(h => h.action === 'closed').at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" className="text-xs h-7">
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Case ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Raised At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insufficiencies.map((insuff) => (
                    <TableRow key={insuff.id}>
                      <TableCell className="font-mono text-xs">{insuff.case_id}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={insuff.type === 'internal' ? 'bg-purple-50' : 'bg-blue-50'}>
                          {insuff.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{insuff.check_type?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-sm">{insuff.reason?.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <Badge className={
                          insuff.status === 'open' ? 'bg-amber-100 text-amber-800' :
                          insuff.status === 'closed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100'
                        }>
                          {insuff.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {insuff.raised_at ? formatDateDDMMYYYY(insuff.raised_at) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6 p-6">
      <h2 className="text-2xl font-bold text-slate-700">Auto-Assign Settings</h2>
      
      {/* Main Auto-Assign Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Auto-Assign Configuration
            </span>
            <Switch
              checked={autoAssignEnabled}
              onCheckedChange={toggleAutoAssign}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            When enabled, newly approved checks will be automatically assigned to agents or vendors based on workload and specialization.
            <strong className="text-amber-600 block mt-2">When disabled, all QC-approved checks will be routed to Operations Manager for manual assignment.</strong>
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Round-Robin Assignment</p>
                <p className="text-sm text-gray-500">Distribute checks evenly among available agents</p>
              </div>
              <Switch 
                checked={autoAssignSettings.round_robin} 
                onCheckedChange={(v) => saveAutoAssignSettings({...autoAssignSettings, round_robin: v})}
                disabled={!autoAssignEnabled}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Respect Specializations</p>
                <p className="text-sm text-gray-500">Match check types with vendor specializations</p>
              </div>
              <Switch 
                checked={autoAssignSettings.respect_specializations}
                onCheckedChange={(v) => saveAutoAssignSettings({...autoAssignSettings, respect_specializations: v})}
                disabled={!autoAssignEnabled}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Workload Balancing</p>
                <p className="text-sm text-gray-500">Consider current workload when assigning</p>
              </div>
              <Switch 
                checked={autoAssignSettings.workload_balancing}
                onCheckedChange={(v) => saveAutoAssignSettings({...autoAssignSettings, workload_balancing: v})}
                disabled={!autoAssignEnabled}
              />
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium mb-2">Max Checks per Agent</p>
              <Input 
                type="number" 
                value={autoAssignSettings.max_checks_per_agent || 20} 
                onChange={(e) => setAutoAssignSettings({...autoAssignSettings, max_checks_per_agent: parseInt(e.target.value) || 20})}
                onBlur={() => saveAutoAssignSettings(autoAssignSettings)}
                className="w-32"
                disabled={!autoAssignEnabled}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* STATE-BASED ASSIGNMENT SETTINGS */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              State-Based Auto-Assignment
            </span>
            <Switch
              checked={autoAssignSettings.state_based_assignment !== false}
              onCheckedChange={(v) => saveAutoAssignSettings({...autoAssignSettings, state_based_assignment: v})}
              disabled={!autoAssignEnabled}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-sm text-gray-600">
            When enabled, <strong>Address, Criminal Court, and Criminal Advocate</strong> checks will be auto-assigned to vendors 
            who cover the candidate's state. This ensures local vendors handle verifications for better accuracy and faster TAT.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="font-medium text-blue-800">Fallback to Pan-India Vendors</p>
                <p className="text-sm text-blue-600">Use pan-India vendors if no state-specific vendor available</p>
              </div>
              <Switch 
                checked={autoAssignSettings.fallback_to_pan_india !== false}
                onCheckedChange={(v) => saveAutoAssignSettings({...autoAssignSettings, fallback_to_pan_india: v})}
                disabled={!autoAssignEnabled || !autoAssignSettings.state_based_assignment}
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="font-medium text-blue-800 mb-1">Affected Check Types</p>
              <div className="flex flex-wrap gap-1">
                <Badge className="bg-blue-100 text-blue-700">Address Verification</Badge>
                <Badge className="bg-blue-100 text-blue-700">Criminal Court</Badge>
                <Badge className="bg-blue-100 text-blue-700">Criminal Advocate</Badge>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              How State-Based Assignment Works
            </h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>System reads candidate's <strong>Present State</strong> from the submission form</li>
              <li>Finds vendors with <strong>State-Specific Coverage</strong> matching that state</li>
              <li>If no match, checks vendors with <strong>Pan-India Coverage</strong> (if enabled)</li>
              <li>Among matching vendors, selects the one with <strong>lowest workload</strong></li>
              <li>Assignment logs record the method used (state_coverage / pan_india_fallback)</li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>📍 Tip:</strong> Configure vendor coverage areas in <strong>Vendor Management → Edit Vendor</strong> to define 
              which states each vendor serves. Set coverage type as "State-Specific" and select the applicable states.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* VERIFICATION MODE SETTINGS */}
      <Card className="border-2 border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-indigo-600" />
              Verification Mode Settings
            </span>
            <Badge variant={verificationModeSettings?.auto_mode_enabled ? 'default' : 'outline'} 
                   className={verificationModeSettings?.auto_mode_enabled ? 'bg-indigo-600' : ''}>
              {verificationModeSettings?.auto_mode_enabled ? 'Auto Mode Available' : 'Manual Mode Only'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-sm text-gray-600">
            Control how agents can verify checks. <strong>Auto Mode</strong> uses AI (Gemini Flash) to analyze uploaded documents 
            and extract verification data automatically. <strong>Manual Mode</strong> requires agents to enter all details manually.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div>
                <p className="font-medium text-indigo-800">Enable Auto Mode (AI)</p>
                <p className="text-sm text-indigo-600">Allow agents to use AI document analysis</p>
              </div>
              <Switch 
                checked={verificationModeSettings?.auto_mode_enabled || false}
                onCheckedChange={(v) => saveVerificationModeSettings({...verificationModeSettings, auto_mode_enabled: v})}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div>
                <p className="font-medium text-indigo-800">Auto-Green on All Verified</p>
                <p className="text-sm text-indigo-600">Set result to "Clear" when all fields verified</p>
              </div>
              <Switch 
                checked={verificationModeSettings?.auto_green_on_all_verified !== false}
                onCheckedChange={(v) => saveVerificationModeSettings({...verificationModeSettings, auto_green_on_all_verified: v})}
              />
            </div>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Verification Remarks (Predefined)
            </h4>
            <div className="flex flex-wrap gap-1 mb-3">
              {(verificationModeSettings?.default_remarks || []).slice(0, 8).map((remark, idx) => (
                <Badge key={idx} variant="outline" className="text-xs bg-white">
                  {remark}
                </Badge>
              ))}
              {(verificationModeSettings?.default_remarks || []).length > 8 && (
                <Badge variant="outline" className="text-xs bg-indigo-100 text-indigo-700">
                  +{verificationModeSettings.default_remarks.length - 8} more
                </Badge>
              )}
            </div>
            <p className="text-xs text-indigo-600">
              Agents can select from these remarks or add custom ones. Custom remarks are saved for future use.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>🤖 AI Analysis:</strong> In Auto Mode, agents can upload HR email screenshots, university PDFs, 
              advocate reports, or online verification screenshots. Gemini Flash extracts data and highlights fields 
              needing manual review.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderCompanyDatabase = () => (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-indigo-700">Company Database</h2>
          <p className="text-slate-600">Employer contacts for employment verification - Auto-initiate when HR email matches</p>
        </div>
        <Badge className="bg-indigo-100 text-indigo-700 text-lg px-4 py-2">
          {companyTemplates.length} Companies
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Company Records
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search companies..." 
                  className="pl-10 w-64"
                  value={dbSearch}
                  onChange={(e) => setDbSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchDatabases(dbSearch)}
                />
              </div>
              <Button onClick={() => fetchDatabases(dbSearch)} variant="outline" size="sm">
                <Search className="w-4 h-4" />
              </Button>
              <Button onClick={() => fetchDatabases()} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-indigo-50/50">
                <TableHead>Company Name</TableHead>
                <TableHead>HR Email</TableHead>
                <TableHead>HR Phone</TableHead>
                <TableHead>Process</TableHead>
                <TableHead>Mandatory Docs</TableHead>
                <TableHead>Auto-Initiate</TableHead>
                <TableHead>MCA CIN</TableHead>
                <TableHead>Data Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companyTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No companies in database</p>
                    <p className="text-sm mt-2">Add companies from Admin panel or search MCA database</p>
                  </TableCell>
                </TableRow>
              ) : (
                companyTemplates.map((company, idx) => (
                  <TableRow key={company.id || idx} className="hover:bg-indigo-50/30">
                    <TableCell className="font-medium">{company.company_name}</TableCell>
                    <TableCell className="text-sm">{company.hr_email || company.contact_email || '-'}</TableCell>
                    <TableCell className="text-sm">{company.contact_phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {company.verification_process || 'email'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {(company.mandatory_documents || []).slice(0, 2).map((doc, i) => (
                          <Badge key={i} variant="outline" className="text-xs capitalize">
                            {doc.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {(company.mandatory_documents || []).length > 2 && (
                          <Badge variant="outline" className="text-xs">+{company.mandatory_documents.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={company.auto_initiate ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {company.auto_initiate ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{company.mca_cin || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {company.data_source || 'manual'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const renderUniversityDatabase = () => (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-rose-700">University Database</h2>
          <p className="text-slate-600">Institution contacts for education verification - Auto-initiate when registrar email matches</p>
        </div>
        <Badge className="bg-rose-100 text-rose-700 text-lg px-4 py-2">
          {institutionTemplates.length} Institutions
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-rose-600" />
              University/College Records
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search universities..." 
                  className="pl-10 w-64"
                  value={dbSearch}
                  onChange={(e) => setDbSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchDatabases(dbSearch)}
                />
              </div>
              <Button onClick={() => fetchDatabases(dbSearch)} variant="outline" size="sm">
                <Search className="w-4 h-4" />
              </Button>
              <Button onClick={() => fetchDatabases()} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-rose-50/50">
                <TableHead>Institution Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Registrar Email</TableHead>
                <TableHead>Contact Phone</TableHead>
                <TableHead>Process</TableHead>
                <TableHead>Auto-Initiate</TableHead>
                <TableHead>UGC Code</TableHead>
                <TableHead>Data Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {institutionTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                    <GraduationCap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No institutions in database</p>
                    <p className="text-sm mt-2">Add universities from Admin panel or search UGC database</p>
                  </TableCell>
                </TableRow>
              ) : (
                institutionTemplates.map((inst, idx) => (
                  <TableRow key={inst.id || idx} className="hover:bg-rose-50/30">
                    <TableCell>
                      <div>
                        <p className="font-medium">{inst.institution_name}</p>
                        {inst.university_name && (
                          <p className="text-xs text-slate-500">{inst.university_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {inst.institution_type || 'university'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{inst.registrar_email || inst.contact_email || '-'}</TableCell>
                    <TableCell className="text-sm">{inst.contact_phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {inst.verification_process || 'email'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={inst.auto_initiate ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {inst.auto_initiate ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{inst.ugc_code || inst.aicte_code || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {inst.data_source || 'manual'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
      case 'checks':
        return renderDashboard();
      case 'completed':
        return renderCompletedGlobal();
      case 'returned-checks':
        return renderReturnedChecks();
      case 'tat-alerts':
        return <TATBreachAlertsTab />;
      case 'vendor-management':
        return renderVendorManagement();
      case 'check-types':
        return renderCheckTypesManagement();
      case 'email-templates':
        return <EmailTemplatesTab />;
      case 'vendor-team':
        return renderVendorTeam();
      case 'internal-team':
        return renderInternalTeam();
      case 'company-db':
        return renderCompanyDatabase();
      case 'university-db':
        return renderUniversityDatabase();
      case 'workload':
        return renderWorkload();
      case 'insufficiencies':
        return renderInsufficiencies();
      case 'form-management':
        return renderFormManagement();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  // Form Management Component
  const renderFormManagement = () => (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-fuchsia-800 flex items-center gap-2">
            <FileCheck className="w-6 h-6" />
            Field Agent Form Management
          </h2>
          <p className="text-gray-500 text-sm mt-1">Create, edit, clone, and map field verification forms to clients</p>
        </div>
        <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={() => { setEditingForm(null); setShowFormEditor(true); }}>
          <PlusCircle className="w-4 h-4 mr-2" />Create New Form
        </Button>
      </div>

      {/* Forms List */}
      <Card className="border-fuchsia-200">
        <CardHeader className="bg-fuchsia-50 border-b border-fuchsia-200">
          <CardTitle className="text-fuchsia-800 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Available Forms ({fieldForms.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-fuchsia-50/50">
                <TableHead>Form Name</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Fields</TableHead>
                <TableHead>Mapped Clients</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fieldForms.map(form => (
                <TableRow key={form.id} className="hover:bg-fuchsia-50/30">
                  <TableCell>
                    <div className="font-medium">{form.name}</div>
                    <div className="text-xs text-gray-500">{form.id}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-purple-100 text-purple-700 capitalize">
                      {form.check_type?.replace(/_/g, ' ') || 'All Types'}
                    </Badge>
                  </TableCell>
                  <TableCell>{form.fields_count || 0} fields</TableCell>
                  <TableCell>
                    <Badge className="bg-blue-100 text-blue-700">
                      {form.mapped_clients || 0} clients
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                      {form.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setEditingForm(form); setShowFormEditor(true); }}
                        className="text-fuchsia-600 hover:bg-fuchsia-50"
                      >
                        <Edit className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleCloneForm(form)}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <Copy className="w-3 h-3 mr-1" />Clone
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setEditingForm(form); setShowFormMappingDialog(true); }}
                        className="text-teal-600 hover:bg-teal-50"
                      >
                        <MapPin className="w-3 h-3 mr-1" />Map
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.open(`/field-verification/preview/${form.id}`, '_blank')}
                        className="text-gray-600 hover:bg-gray-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />Preview
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {fieldForms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No forms created yet</p>
                    <p className="text-sm">Click "Create New Form" to get started</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Client Mappings Section */}
      <Card className="border-teal-200">
        <CardHeader className="bg-teal-50 border-b border-teal-200">
          <CardTitle className="text-teal-800 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Client Form Mappings
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {clients.slice(0, 10).map(client => (
              <div key={client.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <Building2 className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium">{client.company_name || client.name}</p>
                    <p className="text-xs text-gray-500">{client.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select defaultValue="default">
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select Form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default Form</SelectItem>
                      {fieldForms.map(form => (
                        <SelectItem key={form.id} value={form.id}>{form.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="text-teal-600">
                    <CheckCircle className="w-3 h-3 mr-1" />Save
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Form Editor Dialog */}
      <Dialog open={showFormEditor} onOpenChange={setShowFormEditor}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-fuchsia-600" />
              {editingForm ? 'Edit Form' : 'Create New Form'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Form Name</Label>
                <Input 
                  placeholder="e.g., Address Verification Form"
                  defaultValue={editingForm?.name || ''}
                />
              </div>
              <div className="space-y-2">
                <Label>Check Type</Label>
                <Select defaultValue={editingForm?.check_type || 'address_check'}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select check type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHECK_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Form Description</Label>
              <Textarea 
                placeholder="Describe the purpose of this form..."
                defaultValue={editingForm?.description || ''}
              />
            </div>

            {/* Form Fields Section */}
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Form Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">Form field configuration will be available in next update</p>
                  <p className="text-xs mt-1">Currently uses default field verification form fields</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch defaultChecked={editingForm?.is_active ?? true} />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch defaultChecked={editingForm?.requires_ethics ?? true} />
                <Label>Require Ethics Declaration</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch defaultChecked={editingForm?.require_gps ?? true} />
                <Label>Require GPS</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormEditor(false)}>Cancel</Button>
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={handleSaveForm}>
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Form Management Handlers
  const [showFormMappingDialog, setShowFormMappingDialog] = useState(false);
  
  const handleCloneForm = async (form) => {
    const clonedForm = {
      ...form,
      id: `${form.id}_clone_${Date.now()}`,
      name: `${form.name} (Copy)`,
      mapped_clients: 0
    };
    setFieldForms([...fieldForms, clonedForm]);
    toast.success('Form cloned successfully');
  };

  const handleSaveForm = async () => {
    // In a real implementation, this would save to the backend
    toast.success(editingForm ? 'Form updated successfully' : 'Form created successfully');
    setShowFormEditor(false);
    fetchFieldForms();
  };

  // ====================================================================
  // Global Completed Tab (May 30, 2026)
  // Fetches ALL completed checks across every vendor / agent via
  // /team/checks?case_status=completed,report_approved (the same source
  // the Agent Vendor Team's Completed tab uses — gives us
  // resolved_state/city/pincode + vendor_cost out of the box).
  // ====================================================================
  const fetchCompletedGlobal = async () => {
    setCompletedGlobalLoading(true);
    try {
      // Pull all checks across vendors — we don't pass case_status
      // because most COMPLETED checks belong to cases that are still
      // in_progress (other checks on the case are not yet done).
      // Filter on the check.status field instead.
      const res = await axios.get('/team/checks');
      const arr = Array.isArray(res.data) ? res.data : (res.data?.checks || []);
      const COMPLETED = ['verified', 'completed', 'clear', 'report_approved', 'qc_approved'];
      const filtered = arr.filter(c =>
        COMPLETED.includes(c.status) || c.verification_progress === 'completed'
      );
      setCompletedGlobalChecks(filtered);
    } catch (err) {
      console.error('Global Completed fetch failed', err);
      toast.error('Failed to load completed checks');
    } finally {
      setCompletedGlobalLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'completed' && completedGlobalChecks.length === 0 && !completedGlobalLoading) {
      fetchCompletedGlobal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredCompletedGlobal = useMemo(() => {
    let list = completedGlobalChecks;
    if (completedGlobalVendorFilter && completedGlobalVendorFilter !== 'all') {
      list = list.filter(c => {
        const vid = c.assigned_vendor?.vendor_id || c.vendor_id;
        return vid === completedGlobalVendorFilter;
      });
    }
    if (completedGlobalClientFilter && completedGlobalClientFilter !== 'all') {
      list = list.filter(c => c.client_id === completedGlobalClientFilter
        || c.client_name === completedGlobalClientFilter);
    }
    if (completedGlobalCheckTypeFilter && completedGlobalCheckTypeFilter !== 'all') {
      list = list.filter(c => c.check_type === completedGlobalCheckTypeFilter);
    }
    if (!completedGlobalDateFilter || completedGlobalDateFilter === 'latest') return list;
    return list.filter(check => {
      const checkDate = new Date(check.completed_at || check.verified_at || check.updated_at || check.created_at);
      if (isNaN(checkDate.getTime())) return true;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (completedGlobalDateFilter === 'today') {
        return checkDate >= today;
      } else if (completedGlobalDateFilter === 'weekly') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return checkDate >= weekStart;
      } else if (completedGlobalDateFilter === 'last_month') {
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return checkDate >= lastMonthStart && checkDate <= lastMonthEnd;
      } else if (completedGlobalDateFilter === 'between' && completedGlobalDateFrom && completedGlobalDateTo) {
        const from = new Date(completedGlobalDateFrom);
        const to = new Date(completedGlobalDateTo + 'T23:59:59');
        return checkDate >= from && checkDate <= to;
      }
      return true;
    });
  }, [completedGlobalChecks, completedGlobalDateFilter, completedGlobalDateFrom,
      completedGlobalDateTo, completedGlobalVendorFilter, completedGlobalClientFilter,
      completedGlobalCheckTypeFilter]);

  const handleExportCompletedGlobal = async () => {
    setCompletedGlobalExportLoading(true);
    try {
      const dataToExport = filteredCompletedGlobal;
      if (!dataToExport || dataToExport.length === 0) {
        toast.info('No completed checks found for the selected filters');
        setCompletedGlobalExportLoading(false);
        return;
      }
      const fmtDate = (val) => {
        if (!val) return '';
        const d = new Date(val);
        if (isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      };
      const CHECK_TYPE_LABEL = {
        address_check: 'Address',
        employment_check: 'Employment',
        education_check: 'Education',
        criminal_court_check: 'Criminal Court',
        criminal_advocate_check: 'Criminal Advocate',
        court_advocate_check: 'Criminal Advocate',
        id_check: 'ID Verification',
        reference_check: 'Reference',
        personal_reference: 'Personal Reference',
        professional_reference: 'Professional Reference',
        credit_check: 'Credit',
        drug_test: 'Drug Test',
        global_database: 'Global Database',
        world_criminal_db: 'World Database',
        uan_check: 'UAN',
      };
      const sheetLabel = (ct) => {
        const lbl = CHECK_TYPE_LABEL[ct] || (ct || 'Other')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        return lbl.replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Other';
      };
      const buildRow = (check) => {
        const vendorName = check.vendor_name
          || check.assigned_vendor?.vendor_name
          || check.assigned_vendor_name
          || '';
        const vendorCost = check.vendor_cost ?? 0;
        const extraCost = check.extra_cost ?? 0;
        const total = (Number(vendorCost) || 0) + (Number(extraCost) || 0);
        return {
          'Case ID': check.case_id || '',
          'Check ID': check.check_id || check.id || '',
          'Employee Name': check.employee_name || check.candidate_name || '',
          'Client Name': check.client_name || '',
          'Check Type': check.check_type || '',
          'Sub-Check Type': check.sub_option || check.subcheck_type || check.sub_check_type || '',
          'Status': check.status || '',
          'Address': check.resolved_full_address || '',
          'State': check.resolved_state || '',
          'Pin Code': check.resolved_pincode || '',
          'Assigned Date': fmtDate(check.assigned_at || check.vendor_assigned_at),
          'Completed Date': fmtDate(check.completed_at || check.verified_at),
          'TAT Days': check.days_elapsed ?? check.tat_days ?? '',
          'Vendor Name': vendorName,
          'Vendor Cost': Number(vendorCost) || 0,
          'Extra Cost': Number(extraCost) || 0,
          'Total': total,
          'Field Verification Status': check.field_verification_status || '',
          'Field Agent Remarks': check.field_agent_remarks || '',
          'Verification Result': check.verification_result || check.result || '',
          'Field Agent Name': check.field_agent_name || '',
          'Field Agent Phone': check.field_agent_phone || '',
          'Mode of Verification': check.mode_of_verification || '',
        };
      };
      // Education sheet has a dedicated layout — Address/State/Pin Code are
      // not meaningful for an academic verification. Replace columns H/I/J
      // with University & College sourced from the candidate-provided
      // subcheck_data captured at form fill.
      const buildEducationRow = (check) => {
        const sd = check.subcheck_data || {};
        const vendorName = check.vendor_name
          || check.assigned_vendor?.vendor_name
          || check.assigned_vendor_name
          || '';
        const vendorCost = check.vendor_cost ?? 0;
        const extraCost = check.extra_cost ?? 0;
        const total = (Number(vendorCost) || 0) + (Number(extraCost) || 0);
        const university = sd.university_name
          || sd.university
          || check.subcheck_institution
          || check.university_name
          || '';
        const college = sd.college_name
          || sd.college
          || sd.institution_name
          || check.college_name
          || '';
        return {
          'Case ID': check.case_id || '',
          'Check ID': check.check_id || check.id || '',
          'Employee Name': check.employee_name || check.candidate_name || '',
          'Client Name': check.client_name || '',
          'Check Type': check.check_type || '',
          'Sub-Check Type': check.sub_option || check.subcheck_type || check.sub_check_type || '',
          'Status': check.status || '',
          'University': university,
          'College': college,
          'Course': sd.course || check.subcheck_detail || '',
          'Assigned Date': fmtDate(check.assigned_at || check.vendor_assigned_at),
          'Completed Date': fmtDate(check.completed_at || check.verified_at),
          'TAT Days': check.days_elapsed ?? check.tat_days ?? '',
          'Vendor Name': vendorName,
          'Vendor Cost': Number(vendorCost) || 0,
          'Extra Cost': Number(extraCost) || 0,
          'Total': total,
          'Field Verification Status': check.field_verification_status || '',
          'Field Agent Remarks': check.field_agent_remarks || '',
          'Verification Result': check.verification_result || check.result || '',
          'Field Agent Name': check.field_agent_name || '',
          'Field Agent Phone': check.field_agent_phone || '',
          'Mode of Verification': check.mode_of_verification || '',
        };
      };
      const grouped = {};
      dataToExport.forEach((c) => {
        const k = sheetLabel(c.check_type);
        if (!grouped[k]) grouped[k] = [];
        // Use the education-specific row builder for the Education sheet only.
        // All Checks summary keeps the generic Address/State/Pin Code layout.
        const rowBuilder = c.check_type === 'education_check' ? buildEducationRow : buildRow;
        grouped[k].push(rowBuilder(c));
      });
      const wb = XLSX.utils.book_new();
      const wsAll = XLSX.utils.json_to_sheet(dataToExport.map(buildRow));
      XLSX.utils.book_append_sheet(wb, wsAll, 'All Checks');
      Object.keys(grouped).sort().forEach((name) => {
        const ws = XLSX.utils.json_to_sheet(grouped[name]);
        let safe = name;
        let i = 2;
        while (wb.SheetNames.includes(safe)) {
          safe = (name + ' ' + i).slice(0, 31);
          i += 1;
        }
        XLSX.utils.book_append_sheet(wb, ws, safe);
      });
      const filename = `completed_global_${completedGlobalDateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${dataToExport.length} rows across ${Object.keys(grouped).length} check-type sheet(s)`);
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Failed to export data');
    } finally {
      setCompletedGlobalExportLoading(false);
    }
  };

  const renderCompletedGlobal = () => {
    const uniqueClients = Array.from(new Set(
      completedGlobalChecks.map(c => c.client_name).filter(Boolean)
    )).sort();
    // Pull from the global `vendors` state populated on mount — gives
    // every onboarded vendor regardless of whether they have completed
    // checks yet. Falls back to the in-data list if for some reason
    // `vendors` is empty. May 30, 2026 fix.
    const vendorList = (vendors && vendors.length > 0)
      ? vendors
          .filter(v => v && v.id)
          .map(v => [v.id, v.vendor_name || v.name || 'Unknown'])
          .sort((a, b) => a[1].localeCompare(b[1]))
      : Array.from(new Map(
          completedGlobalChecks
            .filter(c => (c.assigned_vendor?.vendor_id || c.vendor_id))
            .map(c => [c.assigned_vendor?.vendor_id || c.vendor_id,
                       c.vendor_name || c.assigned_vendor?.vendor_name || 'Unknown'])
        ).entries()).sort((a, b) => a[1].localeCompare(b[1]));
    const uniqueCheckTypes = Array.from(new Set(
      completedGlobalChecks.map(c => c.check_type).filter(Boolean)
    )).sort();

    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-green-800 flex items-center gap-2">
              <CheckCircle className="w-6 h-6" />
              Completed Checks — Global View
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              All completed verifications across every vendor and agent. Export to multi-sheet Excel for billing reconciliation.
            </p>
          </div>
          <Button variant="outline" onClick={fetchCompletedGlobal} disabled={completedGlobalLoading} data-testid="ops-completed-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${completedGlobalLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Export Section */}
        <Card className="border-2 border-green-100">
          <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="text-lg text-green-800 flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Completed Checks
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-sm font-medium mr-2">Date:</Label>
                {['latest', 'today', 'weekly', 'last_month', 'between'].map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant={completedGlobalDateFilter === type ? 'default' : 'outline'}
                    onClick={() => setCompletedGlobalDateFilter(type)}
                    className={completedGlobalDateFilter === type ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid={`ops-completed-date-${type}`}
                  >
                    {type === 'last_month' ? 'Last Month' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Client:</Label>
                <Select value={completedGlobalClientFilter} onValueChange={setCompletedGlobalClientFilter}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="ops-completed-client-filter">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {uniqueClients.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Vendor:</Label>
                <Select value={completedGlobalVendorFilter} onValueChange={setCompletedGlobalVendorFilter}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="ops-completed-vendor-filter">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendorList.map(([vid, vname]) => (
                      <SelectItem key={vid} value={vid}>{vname}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Check Type:</Label>
                <Select value={completedGlobalCheckTypeFilter} onValueChange={setCompletedGlobalCheckTypeFilter}>
                  <SelectTrigger className="w-[200px] h-9" data-testid="ops-completed-check-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {uniqueCheckTypes.map(t => (
                      <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {completedGlobalDateFilter === 'between' && (
                <div className="flex items-center gap-2">
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={completedGlobalDateFrom}
                      onChange={(e) => setCompletedGlobalDateFrom(e.target.value)} className="w-36" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={completedGlobalDateTo}
                      onChange={(e) => setCompletedGlobalDateTo(e.target.value)} className="w-36" />
                  </div>
                </div>
              )}

              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleExportCompletedGlobal}
                disabled={completedGlobalExportLoading || (completedGlobalDateFilter === 'between' && (!completedGlobalDateFrom || !completedGlobalDateTo))}
                data-testid="ops-export-completed-excel-btn"
              >
                {completedGlobalExportLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Export Excel</>
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Exports the rows shown below (active filters apply). Multi-sheet .xlsx: one worksheet per check type + an All Checks summary. Includes Address, State, Pin Code, Vendor Cost &amp; Total. Dates render as DD-MM-YYYY.
            </p>
          </CardContent>
        </Card>

        {/* Stats summary */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="border-green-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">Total Completed</p>
              <p className="text-2xl font-bold text-green-700">{completedGlobalChecks.length}</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">After Filters</p>
              <p className="text-2xl font-bold text-blue-700">{filteredCompletedGlobal.length}</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">Total Vendor Cost</p>
              <p className="text-2xl font-bold text-purple-700">
                ₹{filteredCompletedGlobal.reduce((s, c) => s + (Number(c.vendor_cost) || 0), 0).toLocaleString('en-IN')}
              </p>
            </CardContent>
          </Card>
          <Card className="border-amber-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">Total + Extra Cost</p>
              <p className="text-2xl font-bold text-amber-700">
                ₹{filteredCompletedGlobal.reduce((s, c) => s + (Number(c.vendor_cost) || 0) + (Number(c.extra_cost) || 0), 0).toLocaleString('en-IN')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card className="border-green-200">
          <CardHeader className="bg-green-50 border-b border-green-200 py-3">
            <CardTitle className="text-green-800 text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Completed Checks ({filteredCompletedGlobal.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {completedGlobalLoading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
            ) : filteredCompletedGlobal.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No completed checks match the current filters.</div>
            ) : (
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader className="bg-gradient-to-r from-green-700 to-emerald-700 sticky top-0 z-10">
                    <TableRow>
                      <TableHead className="text-white text-xs">Case</TableHead>
                      <TableHead className="text-white text-xs">Check ID</TableHead>
                      <TableHead className="text-white text-xs">Employee</TableHead>
                      <TableHead className="text-white text-xs">Client</TableHead>
                      <TableHead className="text-white text-xs">Type</TableHead>
                      <TableHead className="text-white text-xs">Sub</TableHead>
                      <TableHead className="text-white text-xs">State</TableHead>
                      <TableHead className="text-white text-xs">Pin</TableHead>
                      <TableHead className="text-white text-xs">Completed</TableHead>
                      <TableHead className="text-white text-xs">Vendor</TableHead>
                      <TableHead className="text-white text-xs text-right">Cost</TableHead>
                      <TableHead className="text-white text-xs text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompletedGlobal.slice(0, 500).map((c, idx) => {
                      const vendorName = c.vendor_name || c.assigned_vendor?.vendor_name || '';
                      const cost = Number(c.vendor_cost) || 0;
                      const extra = Number(c.extra_cost) || 0;
                      return (
                        <TableRow key={`${c.case_id}-${c.check_id}-${idx}`} className="hover:bg-green-50/40">
                          <TableCell className="text-xs font-mono">{c.case_id}</TableCell>
                          <TableCell className="text-xs font-mono">{c.check_id}</TableCell>
                          <TableCell className="text-xs">{c.employee_name || c.candidate_name}</TableCell>
                          <TableCell className="text-xs">{c.client_name}</TableCell>
                          <TableCell className="text-xs">{(c.check_type || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-xs">{c.sub_option || ''}</TableCell>
                          <TableCell className="text-xs">{c.resolved_state || ''}</TableCell>
                          <TableCell className="text-xs">{c.resolved_pincode || ''}</TableCell>
                          <TableCell className="text-xs">{formatDateDDMMYYYY(c.completed_at || c.verified_at) || ''}</TableCell>
                          <TableCell className="text-xs">{vendorName}</TableCell>
                          <TableCell className="text-xs text-right font-medium">{cost > 0 ? `₹${cost}` : '-'}</TableCell>
                          <TableCell className="text-xs text-right font-bold text-emerald-700">{(cost + extra) > 0 ? `₹${cost + extra}` : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {filteredCompletedGlobal.length > 500 && (
                  <div className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t">
                    Showing first 500 of {filteredCompletedGlobal.length} rows. Use filters to narrow or click Export Excel for the full set.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderReturnedChecks = () => (
    <div className="p-6">
      <Card className="border-amber-200 shadow-sm">
        <CardHeader className="bg-amber-50 border-b border-amber-200">
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <RotateCcw className="w-5 h-5" />
            Returned Checks from Agents/Vendors
            <Badge className="bg-amber-100 text-amber-700 ml-2">{returnedChecks.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-amber-50/50">
                <TableHead>Case ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Returned By</TableHead>
                <TableHead>Return Reason</TableHead>
                <TableHead>Returned Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {returnedChecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <RotateCcw className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No returned checks</p>
                  </TableCell>
                </TableRow>
              ) : (
                returnedChecks.map((check, idx) => (
                  <TableRow key={`${check.case_id}-${check.check_id}-${idx}`} className="hover:bg-amber-50/30">
                    <TableCell className="font-mono text-sm">{check.case_id}</TableCell>
                    <TableCell className="font-medium">{check.employee_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {(check.check_type || '').replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{check.returned_by_name || check.assigned_vendor?.vendor_name || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{check.return_reason || '-'}</TableCell>
                    <TableCell>{check.returned_at ? formatDateDDMMYYYY(check.returned_at) : '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => {
                          setSelectedChecks([check]);
                          setAssignType('agent');
                          setIsReassign(true);
                          setSelectedAssignee('');
                          setShowAssignDialog(true);
                        }}
                      >
                        Reassign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Check Types Management
  const handleCreateCheckType = async () => {
    try {
      if (!newCheckType.name) {
        toast.error('Check type name is required');
        return;
      }
      
      // Auto-generate ID from name if not provided
      const checkId = newCheckType.id || newCheckType.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      
      await axios.post('/check-types', {
        ...newCheckType,
        id: checkId
      });
      
      toast.success('Check type created successfully');
      setShowAddCheckType(false);
      setNewCheckType({
        id: '', name: '', description: '', category: 'verification',
        default_tat: 7, is_expandable: false, requires_documents: true
      });
      fetchCheckTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create check type');
    }
  };

  const handleUpdateCheckTypeTat = async (checkId, newTat) => {
    try {
      await axios.put(`/check-types/${checkId}`, { default_tat: newTat });
      toast.success('TAT updated');
      fetchCheckTypes();
    } catch (error) {
      toast.error('Failed to update TAT');
    }
  };

  const handleUpdateAgentTatBuffer = async (checkId, buffer) => {
    try {
      await axios.put(`/check-types/${checkId}`, { agent_tat_buffer: buffer });
      toast.success('Agent TAT buffer updated');
      fetchCheckTypes();
    } catch (error) {
      toast.error('Failed to update agent TAT buffer');
    }
  };

  const handleToggleCheckTypeActive = async (checkId, isActive) => {
    try {
      await axios.put(`/check-types/${checkId}`, { is_active: isActive });
      toast.success(isActive ? 'Check type activated' : 'Check type deactivated');
      fetchCheckTypes();
    } catch (error) {
      toast.error('Failed to update check type');
    }
  };

  const handleDeleteCheckType = async (checkId) => {
    if (!window.confirm('Are you sure you want to delete this check type?')) return;
    
    try {
      await axios.delete(`/check-types/${checkId}`);
      toast.success('Check type deleted');
      fetchCheckTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete check type');
    }
  };

  const CATEGORY_COLORS = {
    verification: 'bg-blue-100 text-blue-800',
    background: 'bg-purple-100 text-purple-800',
    compliance: 'bg-green-100 text-green-800'
  };

  const renderCheckTypesManagement = () => (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-pink-700">Check Types Management</h2>
          <p className="text-slate-500 text-sm">Configure verification check types and their default TAT</p>
        </div>
        <Button 
          onClick={() => setShowAddCheckType(true)}
          className="bg-pink-600 hover:bg-pink-700"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Add New Check Type
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{checkTypesList.filter(c => c.category === 'verification').length}</div>
            <div className="text-xs text-blue-600">Verification Checks</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-700">{checkTypesList.filter(c => c.category === 'background').length}</div>
            <div className="text-xs text-purple-600">Background Checks</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{checkTypesList.filter(c => c.category === 'compliance').length}</div>
            <div className="text-xs text-green-600">Compliance Checks</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-700">{checkTypesList.filter(c => !c.is_system).length}</div>
            <div className="text-xs text-slate-600">Custom Check Types</div>
          </CardContent>
        </Card>
      </div>

      {/* Check Types Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-pink-50">
                <TableHead>Check Type</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-center">Default TAT</TableHead>
                <TableHead className="text-center">Agent Buffer</TableHead>
                <TableHead className="text-center">Agent TAT</TableHead>
                <TableHead className="text-center">Expandable</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checkTypesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No check types configured</p>
                  </TableCell>
                </TableRow>
              ) : (
                checkTypesList.map((checkType) => (
                  <TableRow key={checkType.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div>
                        <p className="font-semibold">{checkType.name}</p>
                        {checkType.description && (
                          <p className="text-xs text-slate-500">{checkType.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">{checkType.id}</code>
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_COLORS[checkType.category] || 'bg-gray-100 text-gray-800'}>
                        {checkType.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Input
                          type="number"
                          min="1"
                          max="90"
                          value={checkType.default_tat || 7}
                          onChange={(e) => handleUpdateCheckTypeTat(checkType.id, parseInt(e.target.value) || 7)}
                          className="w-16 h-8 text-center"
                        />
                        <span className="text-xs text-slate-500">days</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="30"
                          value={checkType.agent_tat_buffer ?? 2}
                          onChange={(e) => handleUpdateAgentTatBuffer(checkType.id, parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-center"
                        />
                        <span className="text-xs text-slate-500">days</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm font-semibold text-indigo-700">
                        {Math.max((checkType.default_tat || 7) - (checkType.agent_tat_buffer ?? 2), 1)} days
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {checkType.is_expandable ? (
                        <Badge className="bg-green-100 text-green-800">Yes</Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        className={checkType.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                        onClick={() => handleToggleCheckTypeActive(checkType.id, checkType.is_active === false)}
                        style={{ cursor: 'pointer' }}
                      >
                        {checkType.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={checkType.is_system ? 'border-blue-300 text-blue-700' : 'border-pink-300 text-pink-700'}>
                        {checkType.is_system ? 'System' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {!checkType.is_system && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 h-8 w-8 p-0"
                          onClick={() => handleDeleteCheckType(checkType.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add New Check Type Dialog */}
      <Dialog open={showAddCheckType} onOpenChange={setShowAddCheckType}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-pink-600" />
              Add New Check Type
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Check Type Name *</Label>
              <Input
                value={newCheckType.name}
                onChange={(e) => setNewCheckType({...newCheckType, name: e.target.value})}
                placeholder="e.g., Social Media Check"
              />
            </div>
            
            <div>
              <Label>ID (auto-generated if empty)</Label>
              <Input
                value={newCheckType.id}
                onChange={(e) => setNewCheckType({...newCheckType, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                placeholder="e.g., social_media_check"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={newCheckType.description}
                onChange={(e) => setNewCheckType({...newCheckType, description: e.target.value})}
                placeholder="Brief description of this check type"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={newCheckType.category}
                  onValueChange={(v) => setNewCheckType({...newCheckType, category: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verification">Verification</SelectItem>
                    <SelectItem value="background">Background</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Default TAT (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={newCheckType.default_tat}
                  onChange={(e) => setNewCheckType({...newCheckType, default_tat: parseInt(e.target.value) || 7})}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_expandable"
                  checked={newCheckType.is_expandable}
                  onCheckedChange={(checked) => setNewCheckType({...newCheckType, is_expandable: checked})}
                />
                <label htmlFor="is_expandable" className="text-sm">Has sub-checks</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="requires_documents"
                  checked={newCheckType.requires_documents}
                  onCheckedChange={(checked) => setNewCheckType({...newCheckType, requires_documents: checked})}
                />
                <label htmlFor="requires_documents" className="text-sm">Requires documents</label>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddCheckType(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCheckType} className="bg-pink-600 hover:bg-pink-700">
              Create Check Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const ONBOARDING_STATUSES = {
    pending: { label: 'Pending', color: 'bg-gray-100 text-gray-800' },
    documents_pending: { label: 'Docs Pending', color: 'bg-yellow-100 text-yellow-800' },
    verified: { label: 'Verified', color: 'bg-blue-100 text-blue-800' },
    active: { label: 'Active', color: 'bg-green-100 text-green-800' },
    suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800' }
  };

  const renderVendorManagement = () => {
    const filteredVendors = vendors.filter(v => {
      // Status filter
      if (vendorFilter !== 'all' && v.onboarding_status !== vendorFilter) return false;
      // Search filter
      if (vendorSearch) {
        const search = vendorSearch.toLowerCase();
        return (
          (v.name || '').toLowerCase().includes(search) ||
          (v.email || '').toLowerCase().includes(search) ||
          (v.contact_person || '').toLowerCase().includes(search) ||
          (v.phone || '').includes(search)
        );
      }
      return true;
    }).sort((a, b) => {
      switch (vendorSort) {
        case 'name_asc': return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        case 'date_desc': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'date_asc': return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default: return (a.name || '').localeCompare(b.name || '');
      }
    });

    const renderVendorsTab = () => (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowPricingComparison(true)}
              variant="outline"
              className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            >
              <Scale className="w-4 h-4 mr-2" />
              Compare Pricing
            </Button>
            <Button 
              onClick={() => setShowImportDialog(true)}
              variant="outline"
              className="border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Vendors
            </Button>
            <Button 
              onClick={() => { resetVendorForm(); setEditingVendor(null); setShowVendorDialog(true); }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add New Vendor
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="bg-slate-50 border-slate-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('all')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-700">{vendors.length}</div>
              <div className="text-xs text-slate-500">Total Vendors</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('active')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-700">
                {vendors.filter(v => v.onboarding_status === 'active').length}
              </div>
              <div className="text-xs text-green-600">Active</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('pending')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-700">
                {vendors.filter(v => v.onboarding_status === 'pending' || v.onboarding_status === 'documents_pending').length}
              </div>
              <div className="text-xs text-yellow-600">Pending</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('verified')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">
                {vendors.filter(v => v.onboarding_status === 'verified').length}
              </div>
              <div className="text-xs text-blue-600">Verified</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md" onClick={() => setVendorFilter('suspended')}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-700">
                {vendors.filter(v => v.onboarding_status === 'suspended').length}
              </div>
              <div className="text-xs text-red-600">Suspended</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Badge */}
        {vendorFilter !== 'all' && (
          <div className="flex items-center gap-2">
            <Badge className={ONBOARDING_STATUSES[vendorFilter]?.color || 'bg-gray-100'}>
              Showing: {ONBOARDING_STATUSES[vendorFilter]?.label || vendorFilter}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setVendorFilter('all')}>
              <X className="w-4 h-4" /> Clear
            </Button>
          </div>
        )}

        {/* Search & Sort Controls */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendors by name, email, contact..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="vendor-search-input"
            />
          </div>
          <select
            value={vendorSort}
            onChange={(e) => setVendorSort(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="vendor-sort-select"
          >
            <option value="name_asc">Name A-Z</option>
            <option value="name_desc">Name Z-A</option>
            <option value="date_desc">Newest First</option>
            <option value="date_asc">Oldest First</option>
          </select>
          <span className="text-sm text-gray-500">{filteredVendors.length} vendor(s)</span>
        </div>

        {/* Vendors Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-teal-50">
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Coverage</TableHead>
                  <TableHead>Specializations</TableHead>
                  <TableHead>TAT</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No vendors found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVendors.map((vendor) => (
                    <TableRow key={vendor.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-semibold">{vendor.name}</p>
                          <p className="text-xs text-slate-500">{vendor.contact_person}</p>
                          {vendor.gst_number && (
                            <p className="text-xs text-blue-600">GST: {vendor.gst_number}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{vendor.email}</p>
                          <p className="text-slate-500">{vendor.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {vendor.coverage_type === 'pan_india' ? 'Pan India' : 
                           vendor.coverage_states?.length ? `${vendor.coverage_states.length} States` : 'Not Set'}
                        </Badge>
                        {vendor.coverage_states?.length > 0 && vendor.coverage_type !== 'pan_india' && (
                          <p className="text-xs text-slate-500 mt-1 truncate max-w-[150px]" title={vendor.coverage_states.join(', ')}>
                            {vendor.coverage_states.slice(0, 3).join(', ')}{vendor.coverage_states.length > 3 ? '...' : ''}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {vendor.specializations?.slice(0, 2).map(spec => (
                            <Badge key={spec} variant="outline" className="text-xs bg-slate-50">
                              {spec.replace('_', ' ')}
                            </Badge>
                          ))}
                          {vendor.specializations?.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{vendor.specializations.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-blue-100 text-blue-800">{vendor.tat_days}d</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={ONBOARDING_STATUSES[vendor.onboarding_status]?.color || 'bg-gray-100'}>
                          {ONBOARDING_STATUSES[vendor.onboarding_status]?.label || vendor.onboarding_status || 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <p>Assigned: <span className="font-semibold">{vendor.total_assigned || 0}</span></p>
                          <p>Completed: <span className="font-semibold text-green-600">{vendor.total_completed || 0}</span></p>
                          <p>Avg TAT: <span className="font-semibold">{vendor.avg_tat_days?.toFixed(1) || '-'}d</span></p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => openEditVendor(vendor)} title="Edit">
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openContactsDialog(vendor)} 
                            title="Manage Contacts"
                            className="text-indigo-600"
                          >
                            <Users className="w-4 h-4" />
                            {vendor.additional_contacts?.length > 0 && (
                              <span className="ml-1 text-xs">{vendor.additional_contacts.length + 1}</span>
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setSelectedVendorForPricing(vendor);
                              setShowPricingDialog(true);
                            }} 
                            title="Configure Pricing"
                            className="text-green-600"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openTatDialog(vendor)} 
                            title="Configure TAT per Check"
                            className="text-orange-600"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setSelectedVendorForPenalty(vendor);
                              setShowPenaltyDialog(true);
                            }} 
                            title="Configure TAT Penalties"
                            className="text-rose-600"
                          >
                            <Percent className="w-4 h-4" />
                          </Button>
                          {vendor.onboarding_status === 'pending' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-blue-600"
                              onClick={() => handleOnboardVendor(vendor.id, 'verified')}
                              title="Verify"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {vendor.onboarding_status === 'verified' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-green-600"
                              onClick={() => handleOnboardVendor(vendor.id, 'active')}
                              title="Activate"
                            >
                              <Zap className="w-4 h-4" />
                            </Button>
                          )}
                          {vendor.onboarding_status === 'active' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600"
                              onClick={() => handleOnboardVendor(vendor.id, 'suspended')}
                              title="Suspend"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </Button>
                          )}
                          {vendor.onboarding_status === 'suspended' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-green-600"
                              onClick={() => handleOnboardVendor(vendor.id, 'active')}
                              title="Reactivate"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Password & Invite Actions */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-orange-600"
                            onClick={() => handleResetVendorPassword(vendor)}
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-purple-600"
                            onClick={() => handleResendVendorInvite(vendor)}
                            title="Resend Invite"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Vendor Dialog */}
        <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vendor Name *</Label>
                    <Input
                      value={vendorFormData.name}
                      onChange={(e) => setVendorFormData({...vendorFormData, name: e.target.value})}
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <Label>Contact Person *</Label>
                    <Input
                      value={vendorFormData.contact_person}
                      onChange={(e) => setVendorFormData({...vendorFormData, contact_person: e.target.value})}
                      placeholder="Point of Contact"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={vendorFormData.email}
                      onChange={(e) => setVendorFormData({...vendorFormData, email: e.target.value})}
                      placeholder="contact@vendor.com"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={vendorFormData.phone}
                      onChange={(e) => setVendorFormData({...vendorFormData, phone: e.target.value})}
                      placeholder="9876543210"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={vendorFormData.address}
                      onChange={(e) => setVendorFormData({...vendorFormData, address: e.target.value})}
                      placeholder="Office Address"
                    />
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Business Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Vendor Type</Label>
                    <Select
                      value={vendorFormData.vendor_type}
                      onValueChange={(v) => setVendorFormData({...vendorFormData, vendor_type: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subvendor">Subvendor</SelectItem>
                        <SelectItem value="agency_partner">Agency Partner</SelectItem>
                        <SelectItem value="field_agent_vendor">Field Agent Vendor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>GST Number</Label>
                    <Input
                      value={vendorFormData.gst_number}
                      onChange={(e) => setVendorFormData({...vendorFormData, gst_number: e.target.value.toUpperCase()})}
                      placeholder="29ABCDE1234F1Z5"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input
                      value={vendorFormData.pan_number}
                      onChange={(e) => setVendorFormData({...vendorFormData, pan_number: e.target.value.toUpperCase()})}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                  <div>
                    <Label>Default TAT (Days)</Label>
                    <Input
                      type="number"
                      value={vendorFormData.tat_days}
                      onChange={(e) => setVendorFormData({...vendorFormData, tat_days: parseInt(e.target.value) || 7})}
                    />
                  </div>
                </div>
              </div>

              {/* Coverage & Specializations */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Coverage & Specializations</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Coverage Type</Label>
                    <Select
                      value={vendorFormData.coverage_type}
                      onValueChange={(v) => setVendorFormData({...vendorFormData, coverage_type: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pan_india">Pan India</SelectItem>
                        <SelectItem value="regional">Regional (Multiple States)</SelectItem>
                        <SelectItem value="city_specific">City Specific</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {vendorFormData.coverage_type !== 'pan_india' && (
                    <div>
                      <Label>Coverage States</Label>
                      <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white">
                        {INDIAN_STATES.map(state => (
                          <div key={state} className="flex items-center gap-2 py-1">
                            <Checkbox
                              id={`state-${state}`}
                              checked={vendorFormData.coverage_states.includes(state)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setVendorFormData({...vendorFormData, coverage_states: [...vendorFormData.coverage_states, state]});
                                } else {
                                  setVendorFormData({...vendorFormData, coverage_states: vendorFormData.coverage_states.filter(s => s !== state)});
                                }
                              }}
                            />
                            <label htmlFor={`state-${state}`} className="text-sm">{state}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Check Types Handled</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CHECK_TYPES.map(check => (
                      <Badge
                        key={check.value}
                        className={`cursor-pointer ${vendorFormData.specializations.includes(check.value) ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                        onClick={() => {
                          if (vendorFormData.specializations.includes(check.value)) {
                            setVendorFormData({...vendorFormData, specializations: vendorFormData.specializations.filter(s => s !== check.value)});
                          } else {
                            setVendorFormData({...vendorFormData, specializations: [...vendorFormData.specializations, check.value]});
                          }
                        }}
                      >
                        {check.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pricing Configuration Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">
                  Pricing Configuration
                  {(!vendorFormData.check_pricing || vendorFormData.check_pricing.length === 0) && !editingVendor && (
                    <span className="text-red-500 text-xs ml-2">* Required</span>
                  )}
                </h3>
                <div className="space-y-3">
                  {(vendorFormData.check_pricing || []).map((pricing, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <select
                        value={pricing.check_type || ''}
                        onChange={(e) => {
                          const updated = [...(vendorFormData.check_pricing || [])];
                          updated[idx] = { ...updated[idx], check_type: e.target.value };
                          setVendorFormData({ ...vendorFormData, check_pricing: updated });
                        }}
                        className="px-3 py-2 border rounded-md text-sm flex-1"
                        data-testid={`pricing-check-type-${idx}`}
                      >
                        <option value="">Select Check Type</option>
                        {CHECK_TYPES.map(ct => (
                          <option key={ct.value} value={ct.value}>{ct.label}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">&#8377;</span>
                        <Input
                          type="number"
                          placeholder="Price"
                          value={pricing.price || ''}
                          onChange={(e) => {
                            const updated = [...(vendorFormData.check_pricing || [])];
                            updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0 };
                            setVendorFormData({ ...vendorFormData, check_pricing: updated });
                          }}
                          className="w-24"
                          data-testid={`pricing-price-${idx}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          const updated = (vendorFormData.check_pricing || []).filter((_, i) => i !== idx);
                          setVendorFormData({ ...vendorFormData, check_pricing: updated });
                        }}
                        data-testid={`pricing-remove-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setVendorFormData({
                        ...vendorFormData,
                        check_pricing: [...(vendorFormData.check_pricing || []), { check_type: '', price: 0 }]
                      });
                    }}
                    className="w-full text-teal-600 border-teal-300 hover:bg-teal-50"
                    data-testid="add-pricing-entry-btn"
                  >
                    + Add Pricing Entry
                  </Button>
                </div>
              </div>

              {/* Agreement Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Agreement Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Agreement Document</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setVendorFormData({...vendorFormData, agreement_file: file.name});
                            toast.success(`Agreement file "${file.name}" selected`);
                          }
                        }}
                        className="flex-1"
                      />
                      {vendorFormData.agreement_file && (
                        <Badge variant="outline" className="text-green-600">
                          <FileUp className="w-3 h-3 mr-1" />
                          {typeof vendorFormData.agreement_file === 'string' ? vendorFormData.agreement_file : 'Uploaded'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Upload vendor agreement (PDF, DOC)</p>
                  </div>
                  <div>
                    <Label>Agreement Validity</Label>
                    <Input
                      type="date"
                      value={vendorFormData.agreement_validity}
                      onChange={(e) => setVendorFormData({...vendorFormData, agreement_validity: e.target.value})}
                    />
                    <p className="text-xs text-slate-500 mt-1">Agreement expiry date</p>
                  </div>
                </div>
              </div>

              {/* TAT Penalty Configuration */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">TAT Penalty Configuration</h3>
                <p className="text-xs text-slate-500">Configure penalty percentages for TAT breaches. Penalty is deducted from vendor payment.</p>
                <div className="space-y-2">
                  {vendorFormData.tat_penalties?.map((penalty, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                      <span className="text-sm font-medium w-32">
                        {penalty.period_end ? `${penalty.period_start}-${penalty.period_end} days` : `${penalty.period_start}+ days`}
                      </span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={penalty.penalty_percent}
                          onChange={(e) => {
                            const newPenalties = [...vendorFormData.tat_penalties];
                            newPenalties[index].penalty_percent = parseInt(e.target.value) || 0;
                            setVendorFormData({...vendorFormData, tat_penalties: newPenalties});
                          }}
                          className="w-20 h-8"
                        />
                        <span className="text-sm text-slate-600">% penalty</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 ml-auto"
                        onClick={() => {
                          const newPenalties = vendorFormData.tat_penalties.filter((_, i) => i !== index);
                          setVendorFormData({...vendorFormData, tat_penalties: newPenalties});
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lastPenalty = vendorFormData.tat_penalties[vendorFormData.tat_penalties.length - 1];
                      const newStart = lastPenalty ? (lastPenalty.period_end || lastPenalty.period_start) + 1 : 1;
                      setVendorFormData({
                        ...vendorFormData,
                        tat_penalties: [
                          ...vendorFormData.tat_penalties,
                          { period_start: newStart, period_end: newStart + 4, penalty_percent: 5 }
                        ]
                      });
                    }}
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    Add Penalty Tier
                  </Button>
                </div>
              </div>

              {/* Bank Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Bank Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={vendorFormData.bank_name}
                      onChange={(e) => setVendorFormData({...vendorFormData, bank_name: e.target.value})}
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Holder Name</Label>
                    <Input
                      value={vendorFormData.bank_account_holder}
                      onChange={(e) => setVendorFormData({...vendorFormData, bank_account_holder: e.target.value})}
                      placeholder="Company Pvt Ltd"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={vendorFormData.bank_account_number}
                      onChange={(e) => setVendorFormData({...vendorFormData, bank_account_number: e.target.value})}
                      placeholder="50100000000000"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={vendorFormData.bank_ifsc}
                      onChange={(e) => setVendorFormData({...vendorFormData, bank_ifsc: e.target.value.toUpperCase()})}
                      placeholder="HDFC0001234"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Send Portal Invite Option - Only show for new vendors */}
            {!editingVendor && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="send_invite"
                    checked={vendorFormData.send_invite}
                    onChange={(e) => setVendorFormData({...vendorFormData, send_invite: e.target.checked})}
                    className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <Label htmlFor="send_invite" className="text-blue-800 font-medium cursor-pointer">
                      Send Portal Invite Email
                    </Label>
                    <p className="text-xs text-blue-600">
                      Vendor will receive an email with login credentials to access the vendor portal
                    </p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => { setShowVendorDialog(false); setEditingVendor(null); resetVendorForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={editingVendor ? handleUpdateVendor : handleCreateVendor}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {editingVendor ? 'Update Vendor' : 'Create Vendor'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contacts Management Dialog */}
        <Dialog open={showContactsDialog} onOpenChange={setShowContactsDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Manage Contacts - {selectedVendorForContacts?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Existing Contacts List */}
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-700">Contacts ({vendorContacts.length})</h3>
                
                {vendorContacts.map((contact, index) => (
                  <div 
                    key={contact.id || index} 
                    className={`border rounded-lg p-3 ${contact.is_primary ? 'bg-indigo-50 border-indigo-200' : 'bg-white'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{contact.name}</p>
                          {contact.is_primary && (
                            <Badge className="bg-indigo-100 text-indigo-800 text-xs">Primary</Badge>
                          )}
                          {contact.designation && (
                            <span className="text-xs text-slate-500">({contact.designation})</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          <span>{contact.email}</span>
                          {contact.phone && <span className="ml-3">{contact.phone}</span>}
                        </div>
                        {contact.department && (
                          <p className="text-xs text-slate-500 mt-1">Dept: {contact.department}</p>
                        )}
                      </div>
                      
                      {/* CC Options */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Checkbox
                            id={`cc-email-${index}`}
                            checked={contact.cc_emails !== false}
                            onCheckedChange={(checked) => !contact.is_primary && handleToggleContactCC(index, 'cc_emails', checked)}
                            disabled={contact.is_primary}
                          />
                          <label htmlFor={`cc-email-${index}`} className="text-xs text-slate-600">CC Emails</label>
                        </div>
                        <div className="flex items-center gap-1">
                          <Checkbox
                            id={`cc-notif-${index}`}
                            checked={contact.cc_notifications !== false}
                            onCheckedChange={(checked) => !contact.is_primary && handleToggleContactCC(index, 'cc_notifications', checked)}
                            disabled={contact.is_primary}
                          />
                          <label htmlFor={`cc-notif-${index}`} className="text-xs text-slate-600">CC Notifications</label>
                        </div>
                        
                        {!contact.is_primary && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 h-8 w-8 p-0"
                            onClick={() => handleRemoveContact(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add New Contact Form */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-700 mb-3">Add New Contact</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input
                      value={newContact.name}
                      onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                      placeholder="Contact Name"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      placeholder="email@company.com"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={newContact.phone}
                      onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                      placeholder="Phone Number"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Designation</Label>
                    <Input
                      value={newContact.designation}
                      onChange={(e) => setNewContact({...newContact, designation: e.target.value})}
                      placeholder="e.g., Manager, Coordinator"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Department</Label>
                    <Input
                      value={newContact.department}
                      onChange={(e) => setNewContact({...newContact, department: e.target.value})}
                      placeholder="e.g., Operations, Accounts"
                      className="h-9"
                    />
                  </div>
                  <div className="flex items-end gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="new-cc-email"
                        checked={newContact.cc_emails}
                        onCheckedChange={(checked) => setNewContact({...newContact, cc_emails: checked})}
                      />
                      <label htmlFor="new-cc-email" className="text-xs">CC in Emails</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="new-cc-notif"
                        checked={newContact.cc_notifications}
                        onCheckedChange={(checked) => setNewContact({...newContact, cc_notifications: checked})}
                      />
                      <label htmlFor="new-cc-notif" className="text-xs">CC in Notifications</label>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleAddContact}
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700"
                  size="sm"
                >
                  <PlusCircle className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </div>
            </div>

            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={() => { fetchVendors(); setShowContactsDialog(false); }} data-testid="manage-contacts-cancel-btn">
                Cancel
              </Button>
              <Button onClick={handleSaveContacts} className="bg-indigo-600 hover:bg-indigo-700" data-testid="manage-contacts-save-btn">
                <Save className="w-4 h-4 mr-1" /> Save & Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TAT Configuration Dialog */}
        <Dialog open={showTatDialog} onOpenChange={setShowTatDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                TAT Configuration - {selectedVendorForTat?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Set Turn Around Time (TAT) in days for each check type. Default TAT: {selectedVendorForTat?.tat_days || 7} days
              </p>
              
              <div className="border rounded-lg divide-y">
                {CHECK_TYPES.map((checkType) => (
                  <div key={checkType.value} className="flex items-center justify-between p-3 hover:bg-slate-50">
                    <span className="text-sm font-medium">{checkType.label}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={vendorTatConfig[checkType.value] || selectedVendorForTat?.tat_days || 7}
                        onChange={(e) => setVendorTatConfig({
                          ...vendorTatConfig,
                          [checkType.value]: parseInt(e.target.value) || 7
                        })}
                        className="w-20 h-8 text-center"
                      />
                      <span className="text-xs text-slate-500">days</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-amber-50 rounded-lg p-3 text-sm">
                <p className="text-amber-800">
                  <Clock className="w-4 h-4 inline mr-1" />
                  These TAT values will be used for aging calculations in agent dashboards.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowTatDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTatConfig} className="bg-orange-600 hover:bg-orange-700">
                Save TAT Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Vendor Pricing Dialog */}
        <VendorPricingDialog
          vendor={selectedVendorForPricing}
          open={showPricingDialog}
          onClose={() => {
            setShowPricingDialog(false);
            setSelectedVendorForPricing(null);
          }}
          onSave={() => fetchVendors()}
        />

        {/* Vendor Pricing Comparison Dialog */}
        <VendorPricingComparison
          open={showPricingComparison}
          onClose={() => setShowPricingComparison(false)}
        />

        {/* Import Vendors Dialog */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-teal-600" />
                Import Vendors
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Upload an Excel or CSV file to bulk import vendors. Download the template to ensure correct formatting.
              </p>
              
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  // Download template
                  const headers = ['name', 'email', 'phone', 'contact_person', 'address', 'gst_number', 'pan_number', 'coverage_type', 'coverage_states', 'specializations', 'tat_days', 'agreement_validity'];
                  const csvContent = headers.join(',') + '\n' + 'Sample Vendor,vendor@example.com,9876543210,John Doe,123 Street,22AAAAA0000A1Z5,ABCDE1234F,state_specific,Maharashtra|Gujarat,address_check|employment_check,7,2025-12-31';
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'vendor_import_template.csv';
                  a.click();
                  toast.success('Template downloaded');
                }}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setImportFile(file);
                      toast.info(`File "${file.name}" selected for import`);
                    }
                  }}
                  className="mb-2"
                />
                <p className="text-xs text-slate-500">Supported formats: CSV, Excel (.xlsx, .xls)</p>
              </div>
              
              {importFile && (
                <div className="bg-teal-50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileUp className="w-5 h-5 text-teal-600" />
                    <span className="font-medium text-teal-800">{importFile.name}</span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportFile(null); }}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (!importFile) {
                    toast.error('Please select a file to import');
                    return;
                  }
                  toast.success(`Importing vendors from ${importFile.name}...`);
                  // TODO: Implement actual import logic
                  setTimeout(() => {
                    toast.success('Import completed! 0 vendors imported.');
                    setShowImportDialog(false);
                    setImportFile(null);
                  }, 1500);
                }} 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!importFile}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Vendors
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TAT Penalty Dialog */}
        <Dialog open={showPenaltyDialog} onOpenChange={setShowPenaltyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-rose-600" />
                TAT Penalty Configuration - {selectedVendorForPenalty?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Configure penalty percentages for TAT breaches. Penalties are calculated based on days exceeded past the agreed TAT.
              </p>
              
              <div className="space-y-3">
                {(selectedVendorForPenalty?.tat_penalties || [
                  { period_start: 6, period_end: 10, penalty_percent: 5 },
                  { period_start: 11, period_end: 15, penalty_percent: 10 },
                  { period_start: 16, period_end: 20, penalty_percent: 15 },
                  { period_start: 21, period_end: null, penalty_percent: 20 }
                ]).map((penalty, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-700 min-w-[100px]">
                          {penalty.period_end ? `${penalty.period_start}-${penalty.period_end} days` : `${penalty.period_start}+ days`}
                        </span>
                        <span className="text-xs text-slate-500">delay period</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={penalty.penalty_percent}
                        onChange={(e) => {
                          // Update penalty logic would go here
                          toast.info('Update penalty to ' + e.target.value + '%');
                        }}
                        className="w-16 h-8 text-center"
                      />
                      <span className="text-sm font-medium text-rose-600">%</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-rose-50 rounded-lg p-3 text-sm">
                <p className="text-rose-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Penalties will be automatically deducted from vendor invoices based on TAT breach duration.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowPenaltyDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  toast.success('TAT Penalty configuration saved');
                  setShowPenaltyDialog(false);
                }} 
                className="bg-rose-600 hover:bg-rose-700"
              >
                Save Penalties
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );

    // Return the vendors tab content
    return renderVendorsTab();
  };

  const renderVendorTeam = () => (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-violet-700">Agent Vendor Team</h2>
          <p className="text-slate-600">External vendor management for field verifications</p>
        </div>
        <Badge className="bg-violet-100 text-violet-700 text-lg px-4 py-2">
          {vendorTeamChecks.pending_checks || 0} Pending
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-violet-600">{vendorTeamChecks.pending_checks || 0}</div>
            <p className="text-sm text-violet-600">Pending Checks</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-emerald-600">{vendorTeamChecks.completed_checks || 0}</div>
            <p className="text-sm text-emerald-600">Completed</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-blue-600">{vendorTeamChecks.member_count || 0}</div>
            <p className="text-sm text-blue-600">Team Members</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-sm text-slate-600 font-medium mb-2">Check Types:</div>
            <div className="flex flex-wrap gap-1">
              {(vendorTeamChecks.assigned_check_types || []).slice(0, 4).map((ct, i) => (
                <Badge key={i} variant="outline" className="text-xs capitalize">
                  {ct.replace(/_/g, ' ')}
                </Badge>
              ))}
              {(vendorTeamChecks.assigned_check_types || []).length > 4 && (
                <Badge variant="outline" className="text-xs">+{vendorTeamChecks.assigned_check_types.length - 4}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-600" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Assigned Check Types</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(vendorTeamChecks.members || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                    No team members assigned
                  </TableCell>
                </TableRow>
              ) : (
                (vendorTeamChecks.members || []).map((member, idx) => (
                  <TableRow key={member.id || idx}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(vendorTeamChecks.assigned_check_types || []).slice(0, 3).map((ct, i) => (
                          <Badge key={i} variant="outline" className="text-xs capitalize">
                            {ct.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline">View Workload</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const renderInternalTeam = () => (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-cyan-700">Agent Internal Team</h2>
          <p className="text-slate-600">In-house verification for Employment, Education, References</p>
        </div>
        <Badge className="bg-cyan-100 text-cyan-700 text-lg px-4 py-2">
          {internalTeamChecks.pending_checks || 0} Pending
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-cyan-200 bg-cyan-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-cyan-600">{internalTeamChecks.pending_checks || 0}</div>
            <p className="text-sm text-cyan-600">Pending Checks</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-emerald-600">{internalTeamChecks.completed_checks || 0}</div>
            <p className="text-sm text-emerald-600">Completed</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="text-3xl font-bold text-blue-600">{internalTeamChecks.member_count || 0}</div>
            <p className="text-sm text-blue-600">Team Members</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-sm text-slate-600 font-medium mb-2">Check Types:</div>
            <div className="flex flex-wrap gap-1">
              {(internalTeamChecks.assigned_check_types || []).map((ct, i) => (
                <Badge key={i} variant="outline" className="text-xs capitalize">
                  {ct.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-600" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Assigned Check Types</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(internalTeamChecks.members || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                    No team members assigned
                  </TableCell>
                </TableRow>
              ) : (
                (internalTeamChecks.members || []).map((member, idx) => (
                  <TableRow key={member.id || idx}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(internalTeamChecks.assigned_check_types || []).map((ct, i) => (
                          <Badge key={i} variant="outline" className="text-xs capitalize">
                            {ct.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline">View Workload</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header - Colorful Gradient */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Operations Dashboard</h1>
              <p className="text-xs text-white/70">Manage verifications & team assignments</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* User Info */}
          <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2">
            <div className="bg-white/20 p-2 rounded-full">
              <User className="w-5 h-5" />
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">{user?.name || 'Operations Manager'}</p>
              <p className="text-xs text-white/70">{user?.email || 'ops@veriflow.com'}</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-full p-1">
            <NotificationCenter />
          </div>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 border border-white/30" onClick={onLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs Navigation - 2 rows with distinct colors */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b px-6 py-3 shadow-sm">
        <div className="flex flex-wrap gap-2 max-w-full">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            // Handle link items that navigate to different pages
            if (item.isLink && item.href) {
              return (
                <a
                  key={item.id}
                  href={item.href}
                  className={`px-4 py-2 flex items-center gap-2 text-sm font-semibold rounded-lg transition-all duration-200 ${item.bgColor} ${item.textColor} hover:shadow-md border ${item.borderColor}`}
                  data-testid={`tab-${item.id}`}
                >
                  <Icon className={`w-4 h-4 ${item.textColor}`} />
                  <span>{item.label}</span>
                </a>
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`px-4 py-2 flex items-center gap-2 text-sm font-semibold rounded-lg transition-all duration-200 border ${
                  isActive
                    ? `${item.bgColor} ${item.textColor} shadow-md ${item.borderColor} ring-2 ring-offset-1 ring-${item.color}-300`
                    : `bg-white text-gray-600 border-gray-200 hover:${item.bgColor} hover:${item.textColor} hover:border-${item.color}-300`
                }`}
                data-testid={`tab-${item.id}`}
              >
                <Icon className={`w-4 h-4 ${isActive ? item.textColor : 'text-gray-400'}`} />
                <span>{item.label}</span>
                {item.id === 'insufficiencies' && insufficiencies.filter(i => i.status === 'open').length > 0 && (
                  <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5">
                    {insufficiencies.filter(i => i.status === 'open').length}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {renderContent()}
      </main>

      {/* Raise Insufficiency Dialog */}
      <Dialog open={showRaiseInsuff} onOpenChange={setShowRaiseInsuff}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Raise Insufficiency to CRE/QC</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCheck && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Case:</strong> {selectedCheck.case_id}</p>
                <p><strong>Employee:</strong> {selectedCheck.employee_name}</p>
                <p><strong>Check:</strong> {selectedCheck.check_type_display}</p>
              </div>
            )}

            <div>
              <Label>Target Team</Label>
              <Select value={insuffData.targetTeam} onValueChange={(v) => setInsuffData({...insuffData, targetTeam: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cre">CRE (Customer Relations)</SelectItem>
                  <SelectItem value="qc">QC Team</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason</Label>
              <Select value={insuffData.reason} onValueChange={(v) => setInsuffData({...insuffData, reason: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {INSUFFICIENCY_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe what information or documents are needed..."
                value={insuffData.description}
                onChange={(e) => setInsuffData({...insuffData, description: e.target.value})}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRaiseInsuff(false)}>Cancel</Button>
            <Button onClick={handleRaiseInsufficiency}>Raise Insufficiency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isReassign ? 'Reassign Check' : `Assign to ${assignType === 'agent' ? 'Agent' : 'Vendor'}`}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              {isReassign ? 'Reassigning' : 'Assigning'} {selectedChecks.length} check(s)
            </p>
            
            {/* Show tabs only in reassign mode */}
            {isReassign && (
              <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-lg">
                <button
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    assignType === 'agent' 
                      ? 'bg-white shadow text-blue-600' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => {
                    setAssignType('agent');
                    setSelectedAssignee('');
                  }}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Assign to Agent
                </button>
                <button
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    assignType === 'vendor' 
                      ? 'bg-white shadow text-blue-600' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => {
                    setAssignType('vendor');
                    setSelectedAssignee('');
                  }}
                >
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Assign to Vendor
                </button>
              </div>
            )}
            
            <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
              <SelectTrigger>
                <SelectValue placeholder={`Select ${assignType}`} />
              </SelectTrigger>
              <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                {assignType === 'agent' ? (
                  (() => {
                    // Get check types from selected checks
                    const selectedCheckTypes = new Set();
                    selectedChecks.forEach(item => {
                      // Handle both check objects and key strings
                      let check;
                      if (typeof item === 'object' && item.check_type) {
                        check = item;
                      } else {
                        check = checks.find(c => c.row_id === item || `${c.case_id}:${c.check_id}` === item);
                      }
                      if (check?.check_type) {
                        selectedCheckTypes.add(check.check_type);
                      }
                    });
                    
                    // Filter agents who have these check types configured
                    // Helper to normalize check type names for comparison
                    const normalizeCheckType = (type) => {
                      if (!type) return '';
                      // Convert display names to code names
                      const typeMap = {
                        'address verification': 'address_check',
                        'address_check': 'address_check',
                        'criminal check': 'criminal_court_check',
                        'criminal court check': 'criminal_court_check',
                        'criminal_court_check': 'criminal_court_check',
                        'court advocate check': 'criminal_advocate_check',
                        'criminal advocate check': 'criminal_advocate_check',
                        'criminal_advocate_check': 'criminal_advocate_check',
                        'credit check': 'credit_check',
                        'credit_check': 'credit_check',
                        'drug test': 'drug_test',
                        'drug_test': 'drug_test',
                        'id verification': 'id_check',
                        'id check': 'id_check',
                        'id_check': 'id_check',
                        'education verification': 'education_check',
                        'education check': 'education_check',
                        'education_check': 'education_check',
                        'employment verification': 'employment_check',
                        'employment check': 'employment_check',
                        'employment_check': 'employment_check',
                        'reference check': 'reference_check',
                        'reference_check': 'reference_check',
                        'global database': 'global_database',
                        'global database check': 'global_database',
                        'global_database': 'global_database',
                        'world criminal db': 'world_criminal_db',
                        'world_criminal_db': 'world_criminal_db',
                        'india criminal db': 'india_criminal_db',
                        'india_criminal_db': 'india_criminal_db',
                      };
                      const lower = type.toLowerCase().trim().replace(/_/g, ' ');
                      return typeMap[lower] || typeMap[type.toLowerCase().trim()] || type.toLowerCase().replace(/ /g, '_');
                    };

                    // Check types that should only show vendor agents (not internal agents)
                    const vendorOnlyCheckTypes = [
                      'address_check', 'id_check', 'criminal_court_check', 
                      'criminal_advocate_check', 'credit_check', 'global_database',
                      'court_advocate_check', 'world_criminal_db', 'india_criminal_db'
                    ];
                    
                    // Check if any selected check type is vendor-only
                    const isVendorOnlyCheck = Array.from(selectedCheckTypes).some(ct => {
                      const normalizedCt = normalizeCheckType(ct);
                      return vendorOnlyCheckTypes.includes(normalizedCt);
                    });
                    
                    // Filter agents based on check type requirements
                    const filteredAgents = agents.filter(agent => {
                      if (!agent.is_active) return false;
                      
                      // For vendor-only check types, only show vendor agents
                      if (isVendorOnlyCheck) {
                        const isVendorAgent = agent.role === 'agent_vendor' || 
                                              agent.role === 'vendor_management' ||
                                              agent.vendor_id;
                        if (!isVendorAgent) return false;
                      }
                      
                      // For reassignment, show filtered agents (vendor-only if applicable)
                      if (isReassign) return true;
                      
                      const agentCheckTypes = agent.assigned_check_types || [];
                      if (agentCheckTypes.length === 0) return false;
                      
                      const normalizedAgentTypes = agentCheckTypes.map(t => normalizeCheckType(t));
                      return Array.from(selectedCheckTypes).some(ct => {
                        const normalizedCt = normalizeCheckType(ct);
                        return normalizedAgentTypes.includes(normalizedCt);
                      });
                    });
                    
                    if (filteredAgents.length === 0) {
                      // Fallback: show all active agents
                      const allActiveAgents = agents.filter(a => a.is_active);
                      if (allActiveAgents.length > 0) {
                        return allActiveAgents.map(agent => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name} ({agent.email})
                          </SelectItem>
                        ));
                      }
                      return (
                        <SelectItem value="no-agent" disabled>
                          No agents configured for selected check type(s)
                        </SelectItem>
                      );
                    }
                    
                    return filteredAgents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} ({agent.email})
                      </SelectItem>
                    ));
                  })()
                ) : (
                  vendors.map(vendor => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign}>{isReassign ? 'Reassign' : 'Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-initiate Dialog */}
      <Dialog open={showReinitiate} onOpenChange={setShowReinitiate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Re-initiate Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedInsuff && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Case:</strong> {selectedInsuff.case_id}</p>
                <p><strong>Check:</strong> {selectedInsuff.check_type}</p>
                <p><strong>Original Reason:</strong> {selectedInsuff.reason?.replace('_', ' ')}</p>
              </div>
            )}

            <div>
              <Label>Re-initiate To</Label>
              <Select value={reinitiateData.target} onValueChange={(v) => setReinitiateData({...reinitiateData, target: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hr">HR Department</SelectItem>
                  <SelectItem value="university">University/Institution</SelectItem>
                  <SelectItem value="employer">Previous Employer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes / Updated Information</Label>
              <Textarea
                placeholder="Describe the updated documents or information to be sent..."
                value={reinitiateData.notes}
                onChange={(e) => setReinitiateData({...reinitiateData, notes: e.target.value})}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReinitiate(false)}>Cancel</Button>
            <Button onClick={handleReinitiate}>
              <Send className="w-4 h-4 mr-2" />
              Re-initiate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-Down Dialog */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
          {(() => {
            const drillData = getDrillDownData();
            const colorMap = {
              gray: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' },
              blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
              orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
              green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
              red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
              purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
              amber: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
              teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
            };
            const colors = colorMap[drillData.color] || colorMap.gray;
            
            return (
              <>
                <DialogHeader className={`${colors.bg} -m-6 mb-0 p-4 rounded-t-lg border-b ${colors.border}`}>
                  <DialogTitle className={`flex items-center gap-3 ${colors.text}`}>
                    <Badge className={`${colors.bg} ${colors.text} text-lg px-3 py-1`}>
                      {drillData.data.length}
                    </Badge>
                    {drillData.title}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="flex-1 overflow-auto mt-4">
                  {drillData.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No data found for this category</p>
                    </div>
                  ) : drillData.isInsuff ? (
                    /* Insufficiency Table */
                    <Table>
                      <TableHeader>
                        <TableRow className={colors.bg}>
                          <TableHead>Case ID</TableHead>
                          <TableHead>Check ID</TableHead>
                          <TableHead>Check Type</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Raised By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drillData.data.map((item, idx) => (
                          <TableRow key={idx} className="hover:bg-gray-50">
                            <TableCell className="font-mono text-xs">{item.case_id}</TableCell>
                            <TableCell className="font-mono text-xs text-blue-600">{item.check_id || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.check_type?.replace('_', ' ')}</Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{item.reason?.replace('_', ' ')}</TableCell>
                            <TableCell className="text-sm">{item.raised_by || 'Unknown'}</TableCell>
                            <TableCell className="text-xs">
                              {item.raised_at ? formatDateDDMMYYYY(item.raised_at) : 
                               item.cleared_at ? formatDateDDMMYYYY(item.cleared_at) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={item.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'}>
                                {item.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    /* Checks Table */
                    <Table>
                      <TableHeader>
                        <TableRow className={colors.bg}>
                          <TableHead>Case ID</TableHead>
                          <TableHead>Check ID</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Check Type</TableHead>
                          <TableHead>Status</TableHead>
                          {(drillDownType === 'within_tat' || drillDownType === 'out_of_tat') && (
                            <>
                              <TableHead>TAT Days</TableHead>
                              <TableHead>Elapsed</TableHead>
                            </>
                          )}
                          {(drillDownType === 'in_progress' || drillDownType === 'completed' || drillDownType === 'out_of_tat') && (
                            <TableHead>Assigned To</TableHead>
                          )}
                          {drillDownType === 'completed' && <TableHead>Completed</TableHead>}
                          {(drillDownType === 'newly_added' || drillDownType === 'unassigned') && <TableHead>Created</TableHead>}
                          {drillDownType === 'in_progress' && <TableHead>Assigned At</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drillData.data.map((check, idx) => (
                          <TableRow key={idx} className="hover:bg-gray-50">
                            <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                            <TableCell className="font-mono text-xs text-blue-600">{check.check_id || '-'}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{check.employee_name}</p>
                                <p className="text-xs text-gray-500">{check.employee_email?.slice(0, 25)}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{check.client_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{check.check_type_display}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${STATUS_COLORS[check.status] || 'bg-gray-100'}`}>
                                {check.status?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            {(drillDownType === 'within_tat' || drillDownType === 'out_of_tat') && (
                              <>
                                <TableCell className="text-center">{check.tat_days}d</TableCell>
                                <TableCell className={`text-center font-semibold ${check.is_within_tat ? 'text-green-600' : 'text-red-600'}`}>
                                  {check.days_elapsed}d
                                </TableCell>
                              </>
                            )}
                            {(drillDownType === 'in_progress' || drillDownType === 'completed' || drillDownType === 'out_of_tat') && (
                              <TableCell className="text-sm">
                                {check.vendor_name || check.assigned_agent_name || '-'}
                              </TableCell>
                            )}
                            {drillDownType === 'completed' && (
                              <TableCell className="text-xs">
                                {check.completed_at ? formatDateDDMMYYYY(check.completed_at) : '-'}
                              </TableCell>
                            )}
                            {(drillDownType === 'newly_added' || drillDownType === 'unassigned') && (
                              <TableCell className="text-xs">
                                {check.created_at ? new Date(check.created_at).toLocaleDateString() : '-'}
                              </TableCell>
                            )}
                            {drillDownType === 'in_progress' && (
                              <TableCell className="text-xs">
                                {check.assigned_at ? formatDateDDMMYYYY(check.assigned_at) : '-'}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                
                <DialogFooter className="mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Showing {drillData.data.length} record(s)
                  </div>
                  <Button variant="outline" onClick={() => setShowDrillDown(false)}>Close</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Check Details Dialog with Document Preview */}
      <Dialog open={showCheckDetails} onOpenChange={(open) => { if (!open) { setShowCheckDetails(false); setPreviewDoc(null); setCheckDocuments([]); setCheckDetailData(null); setSelectedSubcheck(null); } }}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" />Check Details & Document Review</DialogTitle></DialogHeader>
          {selectedCheck && (
            <div className="flex gap-4" style={{ height: 'calc(95vh - 100px)' }}>
              {/* Left Side: Check Information & Subchecks */}
              <div className={`${previewDoc ? 'w-1/2' : 'w-full'} overflow-auto space-y-3 pr-2`}>
                {/* Basic Info Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs flex items-center gap-2 text-slate-600"><ClipboardList className="w-3 h-3" />Case Info</h4>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-gray-500">Case:</span> <span className="font-mono">{selectedCheck.case_id}</span></div>
                      <div><span className="text-gray-500">Check:</span> <span className="font-mono text-blue-600">{selectedCheck.check_id || selectedCheck.id}</span></div>
                      <div><span className="text-gray-500">Type:</span> <Badge variant="outline" className="text-xs">{selectedCheck.check_type?.replace(/_/g, ' ')}</Badge></div>
                      <div><span className="text-gray-500">Status:</span> <Badge className={`${STATUS_COLORS[selectedCheck.status] || 'bg-gray-100'} text-xs`}>{selectedCheck.status?.replace('_', ' ')}</Badge></div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs flex items-center gap-2 text-slate-600"><User className="w-3 h-3" />Employee</h4>
                    <div className="space-y-1 text-xs">
                      <div className="font-medium">{selectedCheck.employee_name || checkDetailData?.employee?.name}</div>
                      <div className="text-gray-500">{selectedCheck.employee_email || checkDetailData?.employee?.email}</div>
                      <div className="text-gray-500">{selectedCheck.client_name}</div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs flex items-center gap-2 text-slate-600"><Users className="w-3 h-3" />Assignment</h4>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-gray-500">To:</span> {selectedCheck.assigned_agent_name || selectedCheck.vendor_name || 'Unassigned'}</div>
                      <div><Badge className={selectedCheck.is_auto_assigned ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-purple-100 text-purple-700 text-xs'}>{selectedCheck.is_auto_assigned ? 'Auto' : 'Manual'}</Badge></div>
                      <div><span className="text-gray-500">TAT:</span> <Badge className={selectedCheck.is_within_tat ? 'bg-green-100 text-green-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>{selectedCheck.days_elapsed || 0}/{selectedCheck.tat_days || 7}d</Badge></div>
                    </div>
                  </div>
                </div>

                {/* Subchecks Section */}
                {checkDetailData?.employee && (() => {
                  const subchecks = getSubchecks(selectedCheck.check_type, checkDetailData.employee);
                  if (subchecks.length === 0) return null;
                  
                  return (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-semibold mb-2 text-sm flex items-center gap-2 text-amber-800">
                        <ClipboardList className="w-4 h-4" />
                        Sub-Checks ({subchecks.length})
                      </h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {subchecks.map((subcheck, idx) => (
                          <Button
                            key={idx}
                            variant={selectedSubcheck === idx ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedSubcheck(selectedSubcheck === idx ? null : idx)}
                            className={`text-xs ${selectedSubcheck === idx ? 'bg-amber-600' : ''}`}
                          >
                            {subcheck.type}
                          </Button>
                        ))}
                      </div>
                      
                      {selectedSubcheck !== null && subchecks[selectedSubcheck] && (
                        <div className="p-3 bg-white rounded-lg border">
                          <h5 className="font-medium text-sm mb-2 text-amber-700">{subchecks[selectedSubcheck].type} Details</h5>
                          {renderSubcheckDetails(subchecks[selectedSubcheck], selectedCheck.check_type)}
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                {/* Check-specific Data Display (when no subchecks selected) */}
                {checkDetailData?.employee && selectedSubcheck === null && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-sm flex items-center gap-2 text-green-800">
                      <FileText className="w-4 h-4" />
                      {selectedCheck.check_type?.replace(/_/g, ' ')} Data
                    </h4>
                    {(() => {
                      const subchecks = getSubchecks(selectedCheck.check_type, checkDetailData.employee);
                      if (subchecks.length > 0) {
                        return (
                          <div className="space-y-2">
                            {subchecks.map((subcheck, idx) => (
                              <div key={idx} className="p-2 bg-white rounded border cursor-pointer hover:bg-green-100" onClick={() => setSelectedSubcheck(idx)}>
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-sm">{subcheck.type}</span>
                                  <Eye className="w-4 h-4 text-gray-400" />
                                </div>
                                {selectedCheck.check_type === 'address_check' && (
                                  <p className="text-xs text-gray-500 truncate">{subcheck.complete_address}, {subcheck.city}, {subcheck.state}</p>
                                )}
                                {selectedCheck.check_type === 'education_check' && (
                                  <p className="text-xs text-gray-500 truncate">{subcheck.university_name} - {subcheck.course} ({subcheck.year_of_passing})</p>
                                )}
                                {selectedCheck.check_type === 'employment_check' && (
                                  <p className="text-xs text-gray-500 truncate">{subcheck.company_name} - {subcheck.designation} ({subcheck.employed_from} to {subcheck.employed_to || 'Present'})</p>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return <p className="text-sm text-gray-500">Click on a sub-check above to view details</p>;
                    })()}
                  </div>
                )}

                {/* Documents List */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-2 text-sm flex items-center gap-2 text-blue-800"><FileText className="w-4 h-4" />Attached Documents ({checkDocuments.length})</h4>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center py-4"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /><span className="ml-2 text-sm text-slate-500">Loading...</span></div>
                  ) : checkDocuments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No documents attached</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {checkDocuments.map((doc, idx) => (
                        <div 
                          key={doc.id || idx} 
                          className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all text-xs ${previewDoc?.id === doc.id ? 'bg-blue-200 border-2 border-blue-400' : 'bg-white hover:bg-blue-100 border border-blue-100'}`}
                          onClick={() => setPreviewDoc(doc)}
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <span className="truncate">{doc.document_type || doc.original_filename || `Doc ${idx + 1}`}</span>
                          </div>
                          <Eye className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side: Document Preview Panel */}
              {previewDoc && (
                <div className="w-1/2 border-l pl-4 flex flex-col">
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <div>
                      <h4 className="font-semibold">Document Preview</h4>
                      <p className="text-xs text-slate-500">{previewDoc.document_type || previewDoc.original_filename}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => window.open(previewDoc.file_url, '_blank')}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {checkDocuments.length > 1 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {checkDocuments.map((doc, idx) => (
                        <Button key={idx} variant={previewDoc?.id === doc.id ? "default" : "outline"} size="sm" onClick={() => setPreviewDoc(doc)} className="text-xs h-7">
                          {doc.document_type || `Doc ${idx + 1}`}
                        </Button>
                      ))}
                    </div>
                  )}

                  <div className="flex-1 bg-slate-100 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
                    {previewDoc.file_url ? (
                      previewDoc.file_type?.includes('pdf') || previewDoc.file_name?.includes('.pdf') || previewDoc.file_url?.includes('.pdf') ? (
                        <iframe src={previewDoc.file_url} className="w-full h-full" style={{ minHeight: '500px' }} title="Document Preview" />
                      ) : (
                        <div className="h-full flex items-center justify-center p-4">
                          <img src={previewDoc.file_url} alt={previewDoc.original_filename || 'Document'} className="max-w-full max-h-full object-contain rounded shadow-lg" />
                        </div>
                      )
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        <div className="text-center"><FileText className="w-16 h-16 mx-auto mb-4" /><p>No preview available</p></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-Initiate Dialog */}
      <Dialog open={showAutoInitiate} onOpenChange={setShowAutoInitiate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Auto-Initiate Verification
            </DialogTitle>
          </DialogHeader>
          
          {autoInitiateLoading ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
              <p className="text-slate-600">Checking auto-initiate eligibility...</p>
            </div>
          ) : autoInitiateResult ? (
            <div className="space-y-4">
              {/* Check Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Check Type:</span>
                    <p className="font-medium">{autoInitiateCheck?.check_type_display}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Sub-check:</span>
                    <p className="font-medium">{autoInitiateCheck?.subcheck_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Candidate:</span>
                    <p className="font-medium">{autoInitiateCheck?.employee_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Case ID:</span>
                    <p className="font-medium">{autoInitiateCheck?.case_id}</p>
                  </div>
                </div>
              </div>

              {/* Auto-Initiate Result */}
              {autoInitiateResult.can_auto_initiate ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Auto-Initiate Available</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Target Type:</span>
                      <Badge className="bg-blue-100 text-blue-800">
                        {autoInitiateResult.target_type === 'hr_email' ? 'HR Email' : 
                         autoInitiateResult.target_type === 'institution' ? 'Institution' :
                         autoInitiateResult.target_type === 'company_template' ? 'Company Database' :
                         autoInitiateResult.target_type}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Email:</span>
                      <span className="font-medium text-green-700">{autoInitiateResult.target_email}</span>
                    </div>
                    {autoInitiateResult.target_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Recipient:</span>
                        <span className="font-medium">{autoInitiateResult.target_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Sub-check Details */}
                  {autoInitiateResult.subcheck_detail && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-slate-500 mb-2">Verification Details:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {Object.entries(autoInitiateResult.subcheck_detail).map(([key, value]) => 
                          value && (
                            <div key={key}>
                              <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="ml-1 font-medium">{value}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Auto-Initiate Not Available</span>
                    </div>
                    <p className="text-sm text-amber-700">{autoInitiateResult.message}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {autoInitiateResult.check_type === 'employment_check' && 
                        'Tip: Ensure the HR email is provided in the employee\'s employment entry.'}
                      {autoInitiateResult.check_type === 'education_check' && 
                        'Tip: The university must be in the Institution Database with auto-initiate enabled.'}
                      {autoInitiateResult.check_type === 'address_check' && 
                        'Tip: A vendor must be mapped to this state in the vendor settings.'}
                    </p>
                  </div>
                  
                  {/* Manual Contact Entry Section */}
                  {['employment_check', 'education_check'].includes(autoInitiateResult.check_type) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-semibold text-blue-800 mb-3">Enter Contact Manually</p>
                      <p className="text-xs text-blue-600 mb-3">
                        Enter the {autoInitiateResult.check_type === 'employment_check' ? 'HR' : 'Registrar'} email below. 
                        It will be saved to the database for future auto-initiation.
                      </p>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm">Contact Email *</Label>
                          <Input
                            type="email"
                            value={manualContactEmail}
                            onChange={(e) => setManualContactEmail(e.target.value)}
                            placeholder={autoInitiateResult.check_type === 'employment_check' ? 'hr@company.com' : 'registrar@university.edu'}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Contact Name</Label>
                          <Input
                            value={manualContactName}
                            onChange={(e) => setManualContactName(e.target.value)}
                            placeholder="HR Manager / Registrar Name"
                            className="mt-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="saveToDb"
                            checked={saveToDatabase}
                            onCheckedChange={(checked) => setSaveToDatabase(checked)}
                          />
                          <label htmlFor="saveToDb" className="text-xs text-slate-600">
                            Enable auto-initiate for future verifications with this contact
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowAutoInitiate(false); setManualContactEmail(''); setManualContactName(''); }}>
              Cancel
            </Button>
            {autoInitiateResult?.can_auto_initiate ? (
              <Button 
                onClick={handleAutoInitiateEmail}
                disabled={autoInitiateLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {autoInitiateLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Verification Email
              </Button>
            ) : manualContactEmail && ['employment_check', 'education_check'].includes(autoInitiateResult?.check_type) ? (
              <Button 
                onClick={handleSaveContactToDatabase}
                disabled={autoInitiateLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {autoInitiateLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Save Contact to Database
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Document Upload Dialog */}
      <Dialog open={showBulkDocUpload} onOpenChange={setShowBulkDocUpload}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Bulk Document Upload</DialogTitle>
          </DialogHeader>
          <BulkDocumentUpload onClose={() => { setShowBulkDocUpload(false); fetchData(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
